<template>
  <v-container fluid class="pa-6">
    <v-card class="border mb-6 pb-4" elevation="0">
      <v-card-title class="text-h4 font-weight-bold d-flex align-center justify-space-between">
        <div>
          <v-icon icon="mdi-publish" class="mr-2" />
          Unpublished Changes
        </div>
        <div v-if="showPublishActions" class="d-flex ga-2">
          <!-- TEMPORARY:
               Hide Publish Preview while its prediction accuracy is being corrected.
               Do not remove the preview implementation; this should be re-enabled after validation. -->
          <v-btn
            v-if="PUBLISH_PREVIEW_ENABLED"
            variant="outlined"
            size="large"
            :disabled="isPublishInFlight"
            @click="$router.push('/publish-preview')"
          >
            <v-icon icon="mdi-flask-outline" class="mr-2" />
            Preview Before Publishing
          </v-btn>
          <v-btn
            color="primary"
            size="large"
            variant="flat"
            :disabled="isPublishDisabled"
            :loading="isPublishInFlight"
            @click="handlePublishClick"
          >
            <v-icon icon="mdi-publish" class="mr-2" />
            Publish Changes
          </v-btn>
        </div>
      </v-card-title>
      <v-card-subtitle>
        Review and publish your configuration changes to apply them to your inventory processing.
      </v-card-subtitle>
      <v-card-text class="pb-0" v-if="showStatusBanner">
        <v-alert :type="statusType" variant="tonal" density="comfortable" class="mb-0">
          {{ statusMessage }}
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Publishing/processing in-flight (always takes precedence) -->
    <v-card v-if="isPublishInFlight" class="border pa-8 text-center" elevation="0">
      <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
      <div class="text-h6 mb-2">Publishing in progress</div>
      <div class="text-body-2 text-grey">
        Changes have been submitted. This page will show “No unpublished changes” once processing
        completes.
      </div>
    </v-card>

    <!-- Initial load -->
    <v-card v-else-if="initialLoading" class="border pa-8 text-center" elevation="0">
      <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
      <div class="text-h6 mb-2">Loading unpublished changes...</div>
      <div class="text-body-2 text-grey">
        Fetching drafts and configuration changes. This should only take a moment.
      </div>
    </v-card>

    <!-- No changes -->
    <v-card v-else-if="!hasUnpublishedChanges" class="border pa-8 text-center" elevation="0">
      <v-icon icon="mdi-check-circle-outline" size="64" color="success" class="mb-4" />
      <div class="text-h6 mb-2">No unpublished changes</div>
      <div class="text-body-2 text-grey">
        Your configuration is up to date.
      </div>
    </v-card>

    <!-- Changes exist -->
    <div v-else>
      <!-- Summary card -->
      <v-card v-if="showSummaryCard" class="border mb-4" elevation="0">
        <v-card-text>
          <div class="text-subtitle-1 font-weight-bold mb-2">Summary</div>
          <v-chip
            v-if="unpublished?.lastSavedAt"
            class="mr-2"
            size="small"
            variant="tonal"
            color="primary"
          >
            Last saved:
            <DateTimeWithDealerTooltip :value="unpublished.lastSavedAt" />
          </v-chip>
          <v-chip v-if="totalChanges > 0" class="mr-2" size="small" variant="tonal" color="warning">
            {{ totalChanges }} change{{ totalChanges > 1 ? 's' : '' }}
          </v-chip>
        </v-card-text>
      </v-card>

      <!-- Feed Source Configuration (draft-only; promoted on publish) -->
      <v-card
        v-if="feedSourceConfigDraft"
        class="border mb-4"
        elevation="0"
        data-testid="feed-source-config-draft-card"
      >
        <v-card-title class="bg-cyan-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-source-branch" class="mr-2" />
            Feed Source Configuration
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="isReverting['section:feedSourceConfig']"
              :disabled="isPublishInFlight || isRevertingAny"
              data-testid="feed-source-config-revert-section"
              @click="revertFeedSourceConfigSection()"
            >
              Revert section
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="feedSourceConfigDraft.savedAt || feedSourceConfigDraft.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="feedSourceConfigDraft.savedAt">
              Saved <DateTimeWithDealerTooltip :value="feedSourceConfigDraft.savedAt" />
            </span>
            <span v-if="feedSourceConfigDraft.savedBy">
              <span v-if="feedSourceConfigDraft.savedAt"> • </span>
              By {{ feedSourceConfigDraft.savedBy }}
            </span>
          </div>

          <template v-if="feedSourceConfigDraft.diff">
            <div
              v-if="feedSourceConfigDraft.diff.matrix.total > 0"
              class="mb-4"
              data-testid="feed-source-config-matrix-diff"
            >
              <div class="text-subtitle-2 font-weight-bold mb-2">
                Source assignments ({{ feedSourceConfigDraft.diff.matrix.total }})
              </div>
              <ul class="fsc-diff-list pl-4 mb-1">
                <li
                  v-for="(change, idx) in visibleFscMatrixChanges"
                  :key="`fsc-matrix-${idx}`"
                  class="text-body-2 mb-1"
                >
                  {{ change.condition }} · {{ change.state }}:
                  <span class="text-medium-emphasis">{{ formatFscFeedLabel(change.fromFeed) }}</span>
                  →
                  <strong>{{ formatFscFeedLabel(change.toFeed) }}</strong>
                </li>
              </ul>
              <v-btn
                v-if="fscMatrixHasMore"
                variant="text"
                size="small"
                color="primary"
                class="px-0"
                data-testid="feed-source-config-view-all-matrix"
                @click="showAllFscMatrixChanges = !showAllFscMatrixChanges"
              >
                {{ showAllFscMatrixChanges ? 'Show fewer' : 'View all changes' }}
              </v-btn>
            </div>

            <div
              v-if="fscOverrideChangeTotal > 0"
              class="mb-4"
              data-testid="feed-source-config-overrides-diff"
            >
              <div class="text-subtitle-2 font-weight-bold mb-1">Overrides</div>
              <div class="text-body-2 text-medium-emphasis mb-2">
                {{ formatFscChangeCountSummary(feedSourceConfigDraft.diff.overrides) }}
              </div>
              <ul
                v-if="feedSourceConfigDraft.diff.overrides.preview.length"
                class="fsc-diff-list pl-4 mb-0"
              >
                <li
                  v-for="(item, idx) in feedSourceConfigDraft.diff.overrides.preview"
                  :key="`fsc-override-${idx}`"
                  class="text-caption mb-1"
                >
                  {{ item.label }}
                </li>
              </ul>
            </div>

            <div
              v-if="fscFallbackChangeTotal > 0"
              class="mb-4"
              data-testid="feed-source-config-fallbacks-diff"
            >
              <div class="text-subtitle-2 font-weight-bold mb-1">Fallback sources</div>
              <div class="text-body-2 text-medium-emphasis mb-2">
                {{ formatFscChangeCountSummary(feedSourceConfigDraft.diff.fallbacks) }}
              </div>
              <ul
                v-if="feedSourceConfigDraft.diff.fallbacks.preview.length"
                class="fsc-diff-list pl-4 mb-0"
              >
                <li
                  v-for="(item, idx) in feedSourceConfigDraft.diff.fallbacks.preview"
                  :key="`fsc-fallback-${idx}`"
                  class="text-caption mb-1"
                >
                  {{ item.label }}
                </li>
              </ul>
            </div>
          </template>

          <div class="text-caption text-grey mt-2">
            Drafted — takes effect when you publish. Publishing regenerates source-authority
            blocking rules from these assignments.
          </div>
        </v-card-text>
      </v-card>

      <!-- Pause feed-triggered publishes (draft-only; promoted on publish) -->
      <v-card
        v-if="pausePublishByFeedDraft"
        class="border mb-4"
        elevation="0"
        data-testid="pause-publish-by-feed-draft-card"
      >
        <v-card-title class="bg-purple-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-pause-circle-outline" class="mr-2" />
            Pause feed-triggered publishes
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="isReverting['section:pausePublishByFeed']"
              :disabled="isPublishInFlight || isRevertingAny"
              data-testid="pause-publish-by-feed-revert-section"
              @click="revertPausePublishByFeedSection()"
            >
              Revert section
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="iseSettingsDraftMeta?.savedAt || iseSettingsDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="iseSettingsDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="iseSettingsDraftMeta.savedAt" />
            </span>
            <span v-if="iseSettingsDraftMeta?.savedBy">
              <span v-if="iseSettingsDraftMeta?.savedAt"> • </span>
              By {{ iseSettingsDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey mb-4">
            This change is <strong>drafted</strong> and will not affect live processing until you
            publish.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <div class="d-flex flex-wrap ga-2 mb-4">
            <v-chip size="small" variant="tonal">
              {{ pausePublishByFeedDraft.from ? 'Paused' : 'Not paused' }}
            </v-chip>
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip size="small" variant="tonal" color="primary">
              {{ pausePublishByFeedDraft.to ? 'Paused' : 'Not paused' }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Chrome CVD locale (draft-only; promoted on publish) -->
      <v-card
        v-if="chromeCvdLocaleDraft"
        class="border mb-4"
        elevation="0"
        data-testid="chrome-cvd-locale-draft-card"
      >
        <v-card-title class="bg-blue-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-translate" class="mr-2" />
            Chrome CVD language
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:chromeCvdLocale']"
            :disabled="isPublishInFlight || isRevertingAny"
            data-testid="chrome-cvd-locale-revert-section"
            @click="revertChromeCvdLocaleSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-if="iseSettingsDraftMeta?.savedAt || iseSettingsDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="iseSettingsDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="iseSettingsDraftMeta.savedAt" />
            </span>
            <span v-if="iseSettingsDraftMeta?.savedBy">
              <span v-if="iseSettingsDraftMeta?.savedAt"> &bull; </span>
              By {{ iseSettingsDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey mb-4">
            This change is <strong>drafted</strong> and will not affect live processing until you
            publish.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <div class="d-flex flex-wrap ga-2 mb-4">
            <v-chip size="small" variant="tonal">
              {{ formatChromeCvdLocale(chromeCvdLocaleDraft.from) }}
            </v-chip>
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip size="small" variant="tonal" color="primary">
              {{ formatChromeCvdLocale(chromeCvdLocaleDraft.to) }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Inventory alerts (draft-only; promoted on publish) -->
      <v-card
        v-if="inventoryAlertsDraft"
        class="border mb-4"
        elevation="0"
        data-testid="inventory-alerts-draft-card"
      >
        <v-card-title class="bg-teal-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-bell-alert-outline" class="mr-2" />
            Inventory alerts
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:inventoryAlerts']"
            :disabled="isPublishInFlight || isRevertingAny"
            data-testid="inventory-alerts-revert-section"
            @click="revertInventoryAlertsSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-if="iseSettingsDraftMeta?.savedAt || iseSettingsDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="iseSettingsDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="iseSettingsDraftMeta.savedAt" />
            </span>
            <span v-if="iseSettingsDraftMeta?.savedBy">
              <span v-if="iseSettingsDraftMeta?.savedAt"> &bull; </span>
              By {{ iseSettingsDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey mb-4">
            This change is <strong>drafted</strong> and will not affect live processing until you
            publish.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <div class="d-flex flex-wrap ga-2 mb-4">
            <v-chip
              v-for="chip in formatInventoryAlertsChips(inventoryAlertsDraft.from)"
              :key="`from-${chip}`"
              size="small"
              variant="tonal"
            >
              {{ chip }}
            </v-chip>
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="chip in formatInventoryAlertsChips(inventoryAlertsDraft.to)"
              :key="`to-${chip}`"
              size="small"
              variant="tonal"
              color="primary"
            >
              {{ chip }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Ready Courtesy state separation (draft-only; promoted on publish) -->
      <v-card
        v-if="readyCourtesyStateDraft"
        class="border mb-4"
        elevation="0"
        data-testid="ready-courtesy-state-draft-card"
      >
        <v-card-title class="bg-blue-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-car-multiple" class="mr-2" />
            Ready Courtesy vehicle status
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:readyCourtesyState']"
            :disabled="isPublishInFlight || isRevertingAny"
            data-testid="ready-courtesy-state-revert-section"
            @click="revertReadyCourtesyStateSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 text-grey mb-4">
            This change is <strong>drafted</strong> and will take effect after publishing.
          </div>
          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <v-chip size="small" variant="tonal" class="mb-4">
            {{ readyCourtesyStateDraft.from ? 'Separate state' : 'Merged with Ready for Sale' }}
          </v-chip>
          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <v-chip size="small" variant="tonal" color="primary">
            {{ readyCourtesyStateDraft.to ? 'Separate state' : 'Merged with Ready for Sale' }}
          </v-chip>
        </v-card-text>
      </v-card>

      <!-- Shopper Vehicle Status facet (draft-only; promoted on publish) -->
      <v-card
        v-if="vehicleStatusFacetDraft"
        class="border mb-4"
        elevation="0"
        data-testid="vehicle-status-facet-draft-card"
      >
        <v-card-title class="bg-blue-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-format-list-numbered" class="mr-2" />
            Shopper Vehicle Status facet
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:vehicleStatusFacet']"
            :disabled="isPublishInFlight || isRevertingAny"
            data-testid="vehicle-status-facet-revert-section"
            @click="revertVehicleStatusFacetSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 text-grey mb-4">
            This changes only shopper-facing labels and ordering. Inventory statuses remain
            unchanged.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <ol class="mb-4 pl-6">
            <li v-for="option in vehicleStatusFacetDraft.from" :key="`from-${option.value}`">
              {{ option.label }} <span class="text-caption text-grey">({{ option.value }})</span>
            </li>
          </ol>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <ol class="pl-6">
            <li v-for="option in vehicleStatusFacetDraft.to" :key="`to-${option.value}`">
              {{ option.label }} <span class="text-caption text-grey">({{ option.value }})</span>
            </li>
          </ol>
        </v-card-text>
      </v-card>

      <!-- Feed Guardrail settings (draft-only; promoted on publish) -->
      <v-card
        v-if="guardrailDraft"
        class="border mb-4"
        elevation="0"
        data-testid="guardrail-draft-card"
      >
        <v-card-title class="bg-orange-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-shield-alert-outline" class="mr-2" />
            Feed Guardrail — Large Drop Protection
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="isReverting['section:guardrail']"
              :disabled="isPublishInFlight || isRevertingAny"
              data-testid="guardrail-revert-section"
              @click="revertGuardrailSection()"
            >
              Revert section
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="iseSettingsDraftMeta?.savedAt || iseSettingsDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="iseSettingsDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="iseSettingsDraftMeta.savedAt" />
            </span>
            <span v-if="iseSettingsDraftMeta?.savedBy">
              <span v-if="iseSettingsDraftMeta?.savedAt"> &bull; </span>
              By {{ iseSettingsDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey mb-4">
            This change is <strong>drafted</strong> and will not affect live processing until you
            publish.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <div class="d-flex flex-wrap ga-2 mb-4">
            <v-chip size="small" variant="tonal">
              {{ guardrailDraft.fromEnabled ? 'Enabled' : 'Disabled' }}
            </v-chip>
            <v-chip v-if="guardrailDraft.fromEnabled" size="small" variant="tonal">
              Threshold: {{ guardrailDraft.fromThreshold }}%
            </v-chip>
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip size="small" variant="tonal" color="primary">
              {{ guardrailDraft.toEnabled ? 'Enabled' : 'Disabled' }}
            </v-chip>
            <v-chip v-if="guardrailDraft.toEnabled" size="small" variant="tonal" color="primary">
              Threshold: {{ guardrailDraft.toThreshold }}%
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Feed Guardrail settings (draft-only; promoted on publish) -->
      <v-card
        v-if="guardrailDraft"
        class="border mb-4"
        elevation="0"
        data-testid="guardrail-draft-card"
      >
        <v-card-title class="bg-orange-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-shield-alert-outline" class="mr-2" />
            Feed Guardrail — Large Drop Protection
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="isReverting['section:guardrail']"
              :disabled="isPublishInFlight || isRevertingAny"
              data-testid="guardrail-revert-section"
              @click="revertGuardrailSection()"
            >
              Revert section
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="iseSettingsDraftMeta?.savedAt || iseSettingsDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="iseSettingsDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="iseSettingsDraftMeta.savedAt" />
            </span>
            <span v-if="iseSettingsDraftMeta?.savedBy">
              <span v-if="iseSettingsDraftMeta?.savedAt"> &bull; </span>
              By {{ iseSettingsDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey mb-4">
            This change is <strong>drafted</strong> and will not affect live processing until you
            publish.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <div class="d-flex flex-wrap ga-2 mb-4">
            <v-chip size="small" variant="tonal">
              {{ guardrailDraft.fromEnabled ? 'Enabled' : 'Disabled' }}
            </v-chip>
            <v-chip v-if="guardrailDraft.fromEnabled" size="small" variant="tonal">
              Threshold: {{ guardrailDraft.fromThreshold }}%
            </v-chip>
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip size="small" variant="tonal" color="primary">
              {{ guardrailDraft.toEnabled ? 'Enabled' : 'Disabled' }}
            </v-chip>
            <v-chip v-if="guardrailDraft.toEnabled" size="small" variant="tonal" color="primary">
              Threshold: {{ guardrailDraft.toThreshold }}%
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <!-- Pre-Processor Config (draft-only; promoted on publish) -->
      <v-card
        v-if="groupSiteConfigDraft"
        class="border mb-4"
        elevation="0"
        data-testid="group-site-config-draft-card"
      >
        <v-card-title class="bg-teal-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-link-variant" class="mr-2" />
            Pre-Processor Config
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="isReverting['section:groupSiteConfig']"
              :disabled="isPublishInFlight || isRevertingAny"
              data-testid="group-site-config-revert-section"
              @click="revertGroupSiteConfigSection()"
            >
              Revert section
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="groupSiteConfigDraftMeta?.savedAt || groupSiteConfigDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="groupSiteConfigDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="groupSiteConfigDraftMeta.savedAt" />
            </span>
            <span v-if="groupSiteConfigDraftMeta?.savedBy">
              <span v-if="groupSiteConfigDraftMeta?.savedAt"> • </span>
              By {{ groupSiteConfigDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey mb-4">
            Pre-processor settings are <strong>drafted</strong> and will not affect live processing
            until you publish.
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">From</div>
          <div class="d-flex flex-column ga-2 mb-4">
            <v-chip size="small" variant="tonal" class="align-self-start">
              {{ formatPreProcessorMode(groupSiteConfigDraft.from.mode) }}
            </v-chip>
            <template v-if="groupSiteConfigDraft.from.mode === 'groupSite'">
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="(s, i) in groupSiteConfigDraft.from.sources"
                  :key="`gs-from-${i}`"
                  size="small"
                  variant="tonal"
                >
                  {{ s.sourceJuid }}
                  <span v-if="s.filter && Object.keys(s.filter).length" class="ml-1 text-grey">
                    ({{ formatFilter(s.filter) }})
                  </span>
                </v-chip>
                <div
                  v-if="groupSiteConfigDraft.from.sources.length === 0"
                  class="text-body-2 text-grey"
                >
                  No source accounts configured
                </div>
              </div>
            </template>
            <template v-else-if="groupSiteConfigDraft.from.mode === 'sharedInventory'">
              <div class="text-body-2">
                <strong>Pattern:</strong>
                {{ groupSiteConfigDraft.from.pattern || 'None configured' }}
              </div>
              <div class="text-body-2">
                <strong>Feed Source:</strong>
                {{ groupSiteConfigDraft.from.feedSource || 'None configured' }}
              </div>
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="(filter, i) in groupSiteConfigDraft.from.filters"
                  :key="`shared-from-${i}`"
                  size="small"
                  variant="tonal"
                >
                  {{ formatFilter(filter) }}
                </v-chip>
                <div
                  v-if="groupSiteConfigDraft.from.filters.length === 0"
                  class="text-body-2 text-grey"
                >
                  No filters configured
                </div>
              </div>
            </template>
          </div>

          <div class="text-subtitle-2 font-weight-bold mb-2">To</div>
          <div class="d-flex flex-column ga-2">
            <v-chip size="small" variant="tonal" color="primary" class="align-self-start">
              {{ formatPreProcessorMode(groupSiteConfigDraft.to.mode) }}
            </v-chip>
            <template v-if="groupSiteConfigDraft.to.mode === 'groupSite'">
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="(s, i) in groupSiteConfigDraft.to.sources"
                  :key="`gs-to-${i}`"
                  size="small"
                  variant="tonal"
                  color="primary"
                >
                  {{ s.sourceJuid }}
                  <span v-if="s.filter && Object.keys(s.filter).length" class="ml-1">
                    ({{ formatFilter(s.filter) }})
                  </span>
                </v-chip>
                <div
                  v-if="groupSiteConfigDraft.to.sources.length === 0"
                  class="text-body-2 text-grey"
                >
                  No source accounts configured
                </div>
              </div>
            </template>
            <template v-else-if="groupSiteConfigDraft.to.mode === 'sharedInventory'">
              <div class="text-body-2">
                <strong>Pattern:</strong> {{ groupSiteConfigDraft.to.pattern || 'None configured' }}
              </div>
              <div class="text-body-2">
                <strong>Feed Source:</strong>
                {{ groupSiteConfigDraft.to.feedSource || 'None configured' }}
              </div>
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="(filter, i) in groupSiteConfigDraft.to.filters"
                  :key="`shared-to-${i}`"
                  size="small"
                  variant="tonal"
                  color="primary"
                >
                  {{ formatFilter(filter) }}
                </v-chip>
                <div
                  v-if="groupSiteConfigDraft.to.filters.length === 0"
                  class="text-body-2 text-grey"
                >
                  No filters configured
                </div>
              </div>
            </template>
          </div>
        </v-card-text>
      </v-card>

      <!-- Feed Field Rules (draft-only; promoted on publish) -->
      <v-card
        v-if="feedConditionRulesDraftExists"
        class="border mb-4"
        elevation="0"
        data-testid="feed-field-rules-draft-card"
      >
        <v-card-title
          class="bg-teal-lighten-5 mb-2 d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon icon="mdi-swap-horizontal" class="mr-2" />
            Feed Field Rules
          </div>
          <div class="d-flex align-center ga-2">
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="isReverting['section:feedConditionRules']"
              :disabled="isPublishInFlight || isRevertingAny"
              data-testid="feed-field-rules-revert-section"
              @click="revertFeedConditionRulesSection()"
            >
              Revert section
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <div
            v-if="feedConditionRulesDraftMeta?.savedAt || feedConditionRulesDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="feedConditionRulesDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="feedConditionRulesDraftMeta.savedAt" />
            </span>
            <span v-if="feedConditionRulesDraftMeta?.savedBy">
              <span v-if="feedConditionRulesDraftMeta?.savedAt"> &bull; </span>
              By {{ feedConditionRulesDraftMeta.savedBy }}
            </span>
          </div>
          <div class="text-body-2 text-grey">
            Feed field transformation rules are <strong>drafted</strong> and will not affect
            live processing until you publish.
          </div>
        </v-card-text>
      </v-card>

      <!-- Field Mapping Changes -->
      <v-card v-if="fieldMappingChanges" class="border mb-4" elevation="0">
        <v-card-title class="bg-blue-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-file-tree" class="mr-2" />
            Field Mapping
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:fieldMapping']"
            :disabled="isPublishInFlight || isRevertingAny"
            @click="revertFieldMappingSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-if="fieldMappingDraftMeta?.savedAt || fieldMappingDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="fieldMappingDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="fieldMappingDraftMeta.savedAt" />
            </span>
            <span v-if="fieldMappingDraftMeta?.savedBy">
              <span v-if="fieldMappingDraftMeta?.savedAt"> • </span>
              By {{ fieldMappingDraftMeta.savedBy }}
            </span>
          </div>
          <v-chip size="small" class="mr-2" variant="tonal">
            {{ fieldMappingChanges?.totalModified || 0 }} field(s) modified
          </v-chip>

          <v-expansion-panels
            v-if="
              fieldMappingChanges?.segments && Object.keys(fieldMappingChanges.segments).length > 0
            "
            class="border mt-4"
            elevation="0"
          >
            <v-expansion-panel
              v-for="(segmentData, segmentId) in fieldMappingChanges.segments"
              :key="segmentId"
            >
              <v-expansion-panel-title>
                <span class="text-subtitle-2">
                  Segment: <strong>{{ segmentId }}</strong> ({{ segmentData.fieldsCount }} fields)
                </span>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item
                    v-for="(field, idx) in segmentData.modifiedFields"
                    :key="field.fieldKey"
                    class="py-3"
                  >
                    <div class="text-body-2 mb-2">
                      Field: <strong>{{ field.fieldKey }}</strong>
                    </div>
                    <div class="field-mapping-source-change text-caption">
                      <div class="field-mapping-source-change__row">
                        <span class="field-mapping-source-change__label text-medium-emphasis">
                          Previous source:
                        </span>
                        <v-chip
                          v-for="source in fieldMappingSourceList(field.before.sources)"
                          :key="`field-before-${segmentId}-${field.fieldKey}-${source}`"
                          size="x-small"
                          variant="tonal"
                        >
                          {{ formatFieldMappingSource(source) }}
                        </v-chip>
                        <span
                          v-if="fieldMappingSourceList(field.before.sources).length === 0"
                          class="text-medium-emphasis"
                        >
                          None
                        </span>
                        <span
                          v-if="field.before.defaultValue !== undefined"
                          class="text-medium-emphasis"
                        >
                          Default: {{ field.before.defaultValue }}
                        </span>
                      </div>
                      <div class="field-mapping-source-change__row">
                        <span class="field-mapping-source-change__label text-medium-emphasis">
                          New source:
                        </span>
                        <v-chip
                          v-for="source in fieldMappingSourceList(field.after.sources)"
                          :key="`field-after-${segmentId}-${field.fieldKey}-${source}`"
                          size="x-small"
                          variant="tonal"
                          color="primary"
                        >
                          {{ formatFieldMappingSource(source) }}
                        </v-chip>
                        <span
                          v-if="fieldMappingSourceList(field.after.sources).length === 0"
                          class="text-medium-emphasis"
                        >
                          None
                        </span>
                        <span
                          v-if="field.after.defaultValue !== undefined"
                          class="text-medium-emphasis"
                        >
                          Default: {{ field.after.defaultValue }}
                        </span>
                      </div>
                    </div>
                    <template #append>
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        :loading="isReverting[`fieldMapping:${segmentId}:${field.fieldKey}`]"
                        :disabled="isPublishInFlight || isRevertingAny"
                        @click="revertFieldMappingChange({ fieldKey: field.fieldKey, segmentId })"
                      >
                        Revert
                      </v-btn>
                    </template>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <!-- Segments Changes -->
      <v-card v-if="effectiveSegmentsChanges" class="border mb-4" elevation="0">
        <v-card-title class="bg-purple-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-layers-triple-outline" class="mr-2" />
            Groups
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:segments']"
            :disabled="isPublishInFlight || isRevertingAny"
            @click="revertSegmentsSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-if="segmentsDraftMeta?.savedAt || segmentsDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="segmentsDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="segmentsDraftMeta.savedAt" />
            </span>
            <span v-if="segmentsDraftMeta?.savedBy">
              <span v-if="segmentsDraftMeta?.savedAt"> • </span>
              By {{ segmentsDraftMeta.savedBy }}
            </span>
          </div>
          <v-chip size="small" class="mr-2" variant="tonal">
            Total: {{ effectiveSegmentsChanges?.total || 0 }} segment(s)
          </v-chip>
          <v-chip
            v-if="effectiveSegmentsChanges?.added?.length > 0"
            size="small"
            class="mr-2"
            variant="tonal"
            color="success"
          >
            +{{ effectiveSegmentsChanges.added.length }} added
          </v-chip>
          <v-chip
            v-if="effectiveSegmentsChanges?.modified?.length > 0"
            size="small"
            class="mr-2"
            variant="tonal"
            color="warning"
          >
            {{ effectiveSegmentsChanges.modified.length }} modified
          </v-chip>
          <v-chip
            v-if="effectiveSegmentsChanges?.removed?.length > 0"
            size="small"
            class="mr-2"
            variant="tonal"
            color="error"
          >
            -{{ effectiveSegmentsChanges.removed.length }} removed
          </v-chip>

          <v-expansion-panels
            v-model="segmentsPanelsOpen"
            multiple
            class="border mt-4"
            elevation="0"
          >
            <v-expansion-panel v-if="effectiveSegmentsChanges?.added?.length > 0">
              <v-expansion-panel-title
                >Added Segments ({{
                  effectiveSegmentsChanges.added.length
                }})</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item v-for="seg in effectiveSegmentsChanges.added" :key="seg.id">
                    <v-list-item-title>
                      <v-chip size="small" class="mr-2" color="success" variant="tonal">
                        Added
                      </v-chip>
                      <strong>{{ seg.name || seg.id }}</strong>
                      <span class="text-caption text-grey ml-2">({{ seg.id }})</span>
                    </v-list-item-title>
                    <template #append>
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        :loading="isReverting[`segments:added:${seg.id}`]"
                        :disabled="isPublishInFlight || isRevertingAny"
                        @click="revertSegmentChange('added', seg.id)"
                      >
                        Revert
                      </v-btn>
                    </template>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              v-if="effectiveSegmentsChanges?.modified?.length > 0"
              value="segments-modified"
            >
              <v-expansion-panel-title
                >Modified Segments ({{
                  effectiveSegmentsChanges.modified.length
                }})</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item v-for="seg in effectiveSegmentsChanges.modified" :key="seg.id">
                    <div class="w-100 py-2">
                      <div class="d-flex align-center">
                        <v-chip size="small" class="mr-2" color="warning" variant="tonal">
                          Modified
                        </v-chip>
                        <strong>{{ seg.name || seg.id }}</strong>
                        <span class="text-caption text-grey ml-2">({{ seg.id }})</span>
                      </div>

                      <div class="mt-2 text-body-2">
                        <div
                          v-if="segmentDiffLoading[seg.id] || segmentDiffBatchLoading"
                          class="text-grey"
                        >
                          Loading changes…
                        </div>
                        <div v-else-if="segmentDiffError[seg.id]" class="text-error">
                          {{ segmentDiffError[seg.id] }}
                        </div>
                        <div v-else-if="segmentDiffs[seg.id]">
                          <div v-if="segmentDiffs[seg.id]?.nameChanged">
                            <strong>Name</strong>: {{ segmentDiffs[seg.id]?.nameChanged?.from }} →
                            {{ segmentDiffs[seg.id]?.nameChanged?.to }}
                          </div>
                          <div v-if="segmentDiffs[seg.id]?.logicChanged">
                            <strong>Logic</strong>: {{ segmentDiffs[seg.id]?.logicChanged?.from }} →
                            {{ segmentDiffs[seg.id]?.logicChanged?.to }}
                          </div>

                          <div v-if="segmentDiffs[seg.id]?.rulesChanged?.length">
                            <strong>Rules changed</strong> ({{
                              segmentDiffs[seg.id]?.rulesChanged?.length || 0
                            }}):
                            <ul class="ml-4">
                              <li
                                v-for="(c, idx) in segmentDiffs[seg.id]?.rulesChanged || []"
                                :key="`seg-changed-${seg.id}-${idx}`"
                              >
                                {{ prettyRuleChange(c) }}
                              </li>
                            </ul>
                          </div>

                          <div v-if="segmentDiffs[seg.id]?.rulesAdded?.length">
                            <strong>Rules added</strong> ({{
                              segmentDiffs[seg.id]?.rulesAdded?.length || 0
                            }}):
                            <ul class="ml-4">
                              <li
                                v-for="(r, idx) in segmentDiffs[seg.id]?.rulesAdded || []"
                                :key="`seg-added-${seg.id}-${idx}`"
                              >
                                {{ prettyRule(r) }}
                              </li>
                            </ul>
                          </div>
                          <div v-if="segmentDiffs[seg.id]?.rulesRemoved?.length">
                            <strong>Rules removed</strong> ({{
                              segmentDiffs[seg.id]?.rulesRemoved?.length || 0
                            }}):
                            <ul class="ml-4">
                              <li
                                v-for="(r, idx) in segmentDiffs[seg.id]?.rulesRemoved || []"
                                :key="`seg-removed-${seg.id}-${idx}`"
                              >
                                {{ prettyRule(r) }}
                              </li>
                            </ul>
                          </div>

                          <div
                            v-if="
                              !segmentDiffs[seg.id]?.nameChanged &&
                              !segmentDiffs[seg.id]?.logicChanged &&
                              !(segmentDiffs[seg.id]?.rulesChanged?.length || 0) &&
                              !(segmentDiffs[seg.id]?.rulesAdded?.length || 0) &&
                              !(segmentDiffs[seg.id]?.rulesRemoved?.length || 0)
                            "
                          >
                            No semantic differences detected.
                          </div>
                        </div>
                      </div>
                    </div>
                    <template #append>
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        :loading="isReverting[`segments:modified:${seg.id}`]"
                        :disabled="isPublishInFlight || isRevertingAny"
                        @click="revertSegmentChange('modified', seg.id)"
                      >
                        Revert
                      </v-btn>
                    </template>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel v-if="effectiveSegmentsChanges?.removed?.length > 0">
              <v-expansion-panel-title
                >Removed Segments ({{
                  effectiveSegmentsChanges.removed.length
                }})</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-list density="compact">
                  <v-list-item v-for="seg in effectiveSegmentsChanges.removed" :key="seg.id">
                    <v-list-item-title>
                      <v-chip size="small" class="mr-2" color="error" variant="tonal">
                        Removed
                      </v-chip>
                      <strong>{{ seg.name || seg.id }}</strong>
                      <span class="text-caption text-grey ml-2">({{ seg.id }})</span>
                    </v-list-item-title>
                    <template #append>
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        :loading="isReverting[`segments:removed:${seg.id}`]"
                        :disabled="isPublishInFlight || isRevertingAny"
                        @click="revertSegmentChange('removed', seg.id)"
                      >
                        Revert
                      </v-btn>
                    </template>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <!-- Draft Vehicles (Pending Publish) -->
      <v-card v-if="draftVehicles.length > 0" class="border mb-4" elevation="0">
        <v-card-title class="bg-orange-lighten-5 mb-2 d-flex align-center">
          <v-icon icon="mdi-clock-outline" class="mr-2" />
          Draft Vehicles
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            These vehicles are saved but not yet published to your website. They will be published
            when you click "Publish Changes".
          </v-alert>

          <v-expansion-panels v-model="draftVehiclePanelsOpen" class="border" elevation="0" multiple>
            <v-expansion-panel v-for="vehicle in draftVehicles" :key="vehicle.vin" :value="vehicle.vin">
              <v-expansion-panel-title @click="fetchDraftVehicleDetails(vehicle.vin)">
                <div class="d-flex align-center justify-space-between w-100">
                  <div>
                    <v-icon icon="mdi-circle-small" color="orange" size="small" class="mr-2" />
                    <strong>{{ vehicle.vin }}</strong>
                  </div>
                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    :to="{
                      name: 'VehicleDetails',
                      params: { vin: vehicle.vin },
                      query: { from: 'unpublished' },
                    }"
                    @click.stop
                  >
                    View Details
                  </v-btn>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-progress-linear
                  v-if="draftVehicleDetails[vehicle.vin]?.loading"
                  indeterminate
                  class="mb-3"
                />
                <v-alert
                  v-else-if="draftVehicleDetails[vehicle.vin]?.error"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  {{ draftVehicleDetails[vehicle.vin]?.error }}
                </v-alert>
                <div
                  v-else-if="getDraftVehicleDetailRows(vehicle.vin).length > 0"
                  class="d-flex flex-wrap align-center ga-4 text-body-2"
                >
                  <div
                    v-for="row in getDraftVehicleDetailRows(vehicle.vin)"
                    :key="`${vehicle.vin}-${row.label}`"
                    class="d-inline-flex align-baseline"
                  >
                    <span class="text-caption text-grey mr-1">{{ row.label }}:</span>
                    <strong>{{ row.value }}</strong>
                  </div>
                </div>
                <v-alert v-else type="info" variant="tonal" density="compact">
                  Expand to load draft vehicle details, or open the full Vehicle Details page.
                </v-alert>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <!-- Draft Edits (VehicleDraft patches; edit-only drafts) -->
      <v-card v-if="draftEdits.length > 0" class="border mb-4" elevation="0">
        <v-card-title class="bg-orange-lighten-5 mb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-pencil-outline" class="mr-2" />
            Draft Vehicle Edits
          </div>
          <v-btn
            variant="text"
            color="primary"
            size="small"
            :loading="isReverting['section:vehicleDraftEdits']"
            :disabled="isPublishInFlight || isRevertingAny"
            @click="revertVehicleDraftEditsSection"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            These edits are saved as drafts and will not affect your live site until you publish.
          </v-alert>

          <v-expansion-panels class="border" elevation="0" multiple>
            <v-expansion-panel v-for="d in draftEdits" :key="d.vin">
              <v-expansion-panel-title @click="onDraftEditPanelOpen(d)">
                <div class="d-flex align-center justify-space-between w-100">
                  <div>
                    <div class="d-flex align-center">
                      <strong>{{ d.vin }}</strong>
                      <v-chip
                        v-if="getDraftEditIntentBadge(d)"
                        class="ml-2"
                        size="x-small"
                        :color="getDraftEditIntentBadge(d)!.color"
                        variant="tonal"
                      >
                        {{ getDraftEditIntentBadge(d)!.label }}
                      </v-chip>
                      <v-btn
                        class="ml-2"
                        size="x-small"
                        variant="text"
                        density="compact"
                        icon="mdi-open-in-new"
                        :href="`/inventory/${d.vin}?from=unpublished`"
                        target="_blank"
                        rel="noopener"
                        @click.stop
                      />
                    </div>
                    <div class="text-caption text-grey">
                      <span v-if="d.updatedAt">
                        Last saved: <DateTimeWithDealerTooltip :value="d.updatedAt" />
                      </span>
                      <span v-if="d.lastChangedBy" class="ml-3">By: {{ d.lastChangedBy }}</span>
                    </div>
                  </div>

                  <v-btn
                    v-if="d.intent !== 'DELETE'"
                    size="small"
                    variant="text"
                    color="primary"
                    :loading="isReverting[`vehicleDraft:revertPatch:${d.vin}`]"
                    :disabled="isPublishInFlight || isRevertingAny"
                    @click.stop="revertDraftEdit(d.vin)"
                  >
                    Revert
                  </v-btn>

                  <v-btn
                    v-else
                    size="small"
                    variant="text"
                    color="primary"
                    :loading="isReverting[`vehicleDraft:cancelDelete:${d.vin}`]"
                    :disabled="isPublishInFlight || isRevertingAny"
                    @click.stop="cancelStagedDelete(d.vin)"
                  >
                    Revert
                  </v-btn>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-alert
                  v-if="d.intent === 'RESTORE'"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  This vehicle restore is staged. Publish changes to return it to current inventory.
                </v-alert>

                <v-alert
                  v-if="d.intent === 'DELETE'"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  This vehicle removal is staged. Publish changes to remove it from inventory.
                </v-alert>

                <v-alert
                  v-if="draftEditError[d.vin]"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                >
                  {{ draftEditError[d.vin] }}
                </v-alert>

                <v-progress-linear
                  v-if="d.intent !== 'DELETE' && draftEditLoading[d.vin]"
                  indeterminate
                  color="primary"
                  class="mb-3"
                />

                <div v-else-if="d.intent !== 'DELETE'">
                  <div
                    v-if="!hasDraftEditPanelContent(d.vin)"
                    class="text-body-2 text-grey"
                  >
                    No field-level diffs found yet.
                  </div>

                  <v-table
                    v-if="getRenderableDraftEditDiffs(d.vin).length > 0"
                    density="compact"
                    class="mb-4"
                  >
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>From</th>
                        <th>To</th>
                        <th class="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in getRenderableDraftEditDiffs(d.vin)" :key="row.fieldKey">
                        <td>
                          <strong>{{ formatDraftEditFieldLabel(row.fieldKey) }}</strong>
                        </td>
                        <template v-if="row.fieldKey === 'dealer_photos' && row.dealerPhotoDiff">
                          <td colspan="2">
                            <DealerPhotoDiffThumbnails :diff="row.dealerPhotoDiff" />
                          </td>
                          <td class="text-right">
                            <v-btn
                              size="small"
                              variant="text"
                              color="primary"
                              :loading="isReverting[`vehicleDraftField:${d.vin}:${row.fieldKey}`]"
                              :disabled="isPublishInFlight || isRevertingAny"
                              @click.stop="revertDraftEditField(d.vin, row.fieldKey)"
                            >
                              Revert
                            </v-btn>
                          </td>
                        </template>
                        <template v-else>
                          <td>
                            <code>{{ formatDraftEditValue(row) }}</code>
                          </td>
                          <td>
                            <code>{{ formatDraftEditValue(row, 'after') }}</code>
                          </td>
                          <td class="text-right">
                            <v-btn
                              v-if="row.revertible !== false"
                              size="small"
                              variant="text"
                              color="primary"
                              :loading="isReverting[`vehicleDraftField:${d.vin}:${row.fieldKey}`]"
                              :disabled="isPublishInFlight || isRevertingAny"
                              @click.stop="revertDraftEditField(d.vin, row.fieldKey)"
                            >
                              Revert
                            </v-btn>
                            <span v-else class="text-caption text-grey">Use vehicle revert</span>
                          </td>
                        </template>
                      </tr>
                    </tbody>
                  </v-table>

                  <div v-if="getDraftManualOverrideRows(d.vin).length > 0">
                    <div class="text-subtitle-2 font-weight-bold mb-2">Pending manual overrides</div>
                    <VehicleDraftManualOverridesTable
                      :vin="d.vin"
                      :rows="getDraftManualOverrideRows(d.vin)"
                      :dealer-time-zone="dealerTimeZone"
                      :live-photos="getDraftOverrideLivePhotos(d.vin)"
                      photo-mode="diff"
                      show-revert
                      :is-reverting="isReverting"
                      :revert-disabled="isPublishInFlight || isRevertingAny"
                      @revert="(fieldKey) => revertManualOverrideDraftActionField(d.vin, fieldKey)"
                    />
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <!-- Publish Rules Changes -->
      <v-card v-if="listingRulesChanges" class="border mb-4" elevation="0">
        <v-card-title class="mb-2 bg-green-lighten-5 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon icon="mdi-format-list-checks" class="mr-2" />
            Publish Rules
          </div>
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isReverting['section:listingRules']"
            :disabled="isPublishInFlight || isRevertingAny"
            @click="revertListingRulesSection()"
          >
            Revert section
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-if="listingRulesDraftMeta?.savedAt || listingRulesDraftMeta?.savedBy"
            class="text-caption text-grey mb-2"
          >
            <span v-if="listingRulesDraftMeta?.savedAt">
              Saved <DateTimeWithDealerTooltip :value="listingRulesDraftMeta.savedAt" />
            </span>
            <span v-if="listingRulesDraftMeta?.savedBy">
              <span v-if="listingRulesDraftMeta?.savedAt"> • </span>
              By {{ listingRulesDraftMeta.savedBy }}
            </span>
          </div>
          <v-chip size="small" class="mr-2" variant="tonal">
            Total: {{ listingRulesChanges?.totalRules || 0 }} rule(s)
          </v-chip>
          <v-chip
            v-if="listingRulesChanges?.totalRulesAdded > 0"
            size="small"
            class="mr-2"
            variant="tonal"
            color="success"
          >
            +{{ listingRulesChanges.totalRulesAdded }} added
          </v-chip>
          <v-chip
            v-if="listingRulesChanges?.totalRulesRemoved > 0"
            size="small"
            class="mr-2"
            variant="tonal"
            color="error"
          >
            -{{ listingRulesChanges.totalRulesRemoved }} removed
          </v-chip>

          <v-expansion-panels
            v-if="listingRulesChanges?.segments"
            class="border mt-4"
            elevation="0"
          >
            <v-expansion-panel
              v-for="(segmentData, segmentId) in listingRulesChanges.segments"
              :key="segmentId"
            >
              <v-expansion-panel-title>
                Segment: <strong class="ml-2">{{ segmentId }}</strong>
                <v-spacer />
                <v-chip size="x-small" class="mr-2" variant="tonal">
                  {{ segmentData.rulesCount }} rules
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Added Rules -->
                <div v-if="segmentData.addedRules?.length > 0" class="mb-4">
                  <div class="text-subtitle-2 font-weight-bold text-success mb-2">
                    Added Rules ({{ segmentData.addedRules.length }})
                  </div>
                  <v-list density="compact">
                    <v-list-item
                      v-for="(rule, idx) in segmentData.addedRules"
                      :key="rule.opId || idx"
                    >
                      <v-list-item-title>
                        <v-icon icon="mdi-plus-circle" color="success" size="small" class="mr-2" />
                        <strong>{{ rule.field }}</strong> {{ rule.operator }} "{{ rule.value }}"
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        Action: {{ rule.action }}
                        {{ rule.reason ? `| Reason: ${rule.reason}` : '' }}
                      </v-list-item-subtitle>
                      <template #append>
                        <v-btn
                          size="small"
                          variant="text"
                          color="primary"
                          :loading="
                            isReverting[
                              rule.opId
                                ? `listingRules:${segmentId}:added:${rule.opId}`
                                : `listingRules:${segmentId}:added:${idx}`
                            ]
                          "
                          :disabled="isPublishInFlight || isRevertingAny"
                          @click="
                            revertListingRuleChange('added', String(segmentId), rule, Number(idx))
                          "
                        >
                          Revert
                        </v-btn>
                      </template>
                    </v-list-item>
                  </v-list>
                </div>

                <!-- Modified Rules -->
                <div v-if="segmentData.modifiedRules?.length > 0" class="mb-4">
                  <div class="text-subtitle-2 font-weight-bold text-warning mb-2">
                    Modified Rules ({{ segmentData.modifiedRules.length }})
                  </div>
                  <v-list density="compact">
                    <v-list-item
                      v-for="(rule, idx) in segmentData.modifiedRules"
                      :key="rule.opId || idx"
                    >
                      <v-list-item-title class="text-caption">
                        <v-icon
                          icon="mdi-pencil-circle"
                          color="warning"
                          size="small"
                          class="mr-2"
                        />
                        Before: <strong>{{ rule.before.field }}</strong>
                        {{ rule.before.operator }} "{{ rule.before.value }}"
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        After: <strong>{{ rule.after.field }}</strong> {{ rule.after.operator }} "{{
                          rule.after.value
                        }}"
                      </v-list-item-subtitle>
                      <template #append>
                        <v-btn
                          size="small"
                          variant="text"
                          color="primary"
                          :loading="
                            isReverting[
                              rule.opId
                                ? `listingRules:${segmentId}:modified:${rule.opId}`
                                : `listingRules:${segmentId}:modified:${idx}`
                            ]
                          "
                          :disabled="isPublishInFlight || isRevertingAny"
                          @click="
                            revertListingRuleChange(
                              'modified',
                              String(segmentId),
                              rule,
                              Number(idx),
                            )
                          "
                        >
                          Revert
                        </v-btn>
                      </template>
                    </v-list-item>
                  </v-list>
                </div>

                <!-- Removed Rules -->
                <div v-if="segmentData.removedRules?.length > 0">
                  <div class="text-subtitle-2 font-weight-bold text-error mb-2">
                    Removed Rules ({{ segmentData.removedRules.length }})
                  </div>
                  <v-list density="compact">
                    <v-list-item
                      v-for="(rule, idx) in segmentData.removedRules"
                      :key="rule.opId || idx"
                    >
                      <v-list-item-title>
                        <v-icon icon="mdi-minus-circle" color="error" size="small" class="mr-2" />
                        <strong>{{ rule.field }}</strong> {{ rule.operator }} "{{ rule.value }}"
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        Action: {{ rule.action }}
                        {{ rule.reason ? `| Reason: ${rule.reason}` : '' }}
                      </v-list-item-subtitle>
                      <template #append>
                        <v-btn
                          size="small"
                          variant="text"
                          color="primary"
                          :loading="
                            isReverting[
                              rule.opId
                                ? `listingRules:${segmentId}:removed:${rule.opId}`
                                : `listingRules:${segmentId}:removed:${idx}`
                            ]
                          "
                          :disabled="isPublishInFlight || isRevertingAny"
                          @click="
                            revertListingRuleChange('removed', String(segmentId), rule, Number(idx))
                          "
                        >
                          Revert
                        </v-btn>
                      </template>
                    </v-list-item>
                  </v-list>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </div>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3500">
      {{ snackbar.message }}
    </v-snackbar>

    <PublishWithoutPreviewDialog
      v-if="PUBLISH_PREVIEW_ENABLED"
      v-model="showPublishWithoutPreviewDialog"
      :status="previewStore.status"
      @cancel="showPublishWithoutPreviewDialog = false"
      @run-preview="handleRunPreviewFromDialog"
      @publish-anyway="handlePublishAnyway"
    />
  </v-container>
</template>

<script lang="ts" setup>
import { ref, computed, unref, inject, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useProductSettingsStore } from '@/stores/productSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePublishPreviewStore } from '@/stores/usePublishPreviewStore'
import { PUBLISH_PREVIEW_ENABLED } from '@/constants/publishPreviewVisibility'
import PublishWithoutPreviewDialog from '@/components/publishPreview/PublishWithoutPreviewDialog.vue'

import type { UnpublishedState } from '@/types/appTypes'
import type { Ref } from 'vue'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import {
  fetchUnpublishedChangesSummary,
  checkPipelineStatus,
  checkPublishLockStatus,
  type UnpublishedChangesSummary,
} from '@/services/publishService'
import {
  isTerminalPublishStatus,
  shouldClearActivePublishForUnlockedStatus,
} from '@/utils/publishPollingDecision'
import { fetchSegmentationConfig } from '@/services/segmentationConfigService'
import { diffSegment, type FlatSegmentRule, type FlatSegmentRuleChange } from '@/utils/segmentDiff'
import { compareFieldMappings, compareListingRules, compareSegments } from '@/utils/compareConfigs'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'
import VehicleDraftManualOverridesTable from '@/components/VehicleDraftManualOverridesTable.vue'
import DealerPhotoDiffThumbnails from '@/components/DealerPhotoDiffThumbnails.vue'
import { DEFAULT_LISTING_RULES, useListingConfigStore } from '@/stores/listingConfig'
import { cleanupListingRules } from '@/utils/listingRules'
import {
  cancelVehicleDeleteDraft,
  fetchVehicleByVin,
  fetchVehicleDraft,
  formatVehicleDraftIntentChipLabel,
  getVehicleDraftIntentChip,
  resolveVehicleDraftIntent,
  revertVehicleDraft,
  revertVehicleDraftField,
  revertManualOverrideDraftAction,
  type InventoryState,
  type InventoryUsageType,
  type VehicleDraftIntent,
  type WebsiteVisibility,
  type WebsiteVisibilityMode,
} from '@/services/inventory'
import {
  buildManualOverrideDraftDisplayRows,
  countDraftVisibleChanges,
  formatManualOverrideDraftFieldValue,
  formatVehicleDraftFieldLabel,
  type ManualOverrideDraftDisplayRow,
} from '@/utils/manualOverrideDraftDisplay'
import {
  deleteIseSettingsDraft,
  fetchIsePrimarySources,
  inventoryAlertsSettingsEqual,
  normalizeInventoryAlertsSettings,
  type ChromeCvdLocale,
  type InventoryAlertsSettings,
  type VehicleStatusFacetOption,
} from '@/services/iseSettingsService'
import { fetchFeedMappingDraftSummary } from '@/services/feedMappingService'
import type { FeedMappingDraftSummary } from '@/types/feedMapping'
import {
  deleteSegmentDraftPatch,
  deleteSegmentsDraft,
  fetchFieldMappingDraft,
  fetchListingRulesDraft,
  deleteListingRuleOp,
  deleteListingRulesDraft,
  deleteFieldMappingV3Field,
} from '@/services/configDraftService'
import { shouldClearPersistedUnpublished } from '@/utils/unpublishedReconcile'
import {
  deleteGroupSiteConfigDraft,
  fetchGroupSiteConfig,
  type GroupSiteConfigResponse,
  type GroupSiteFilter,
  type GroupSiteSourceEntry,
  type PreProcessorMode,
} from '@/services/groupSiteConfigService'

interface NormalizedPreProcessorConfig {
  mode: PreProcessorMode
  sources: GroupSiteSourceEntry[]
  pattern: string
  feedSource: string
  filters: GroupSiteFilter[]
  source: string
  savedAt?: string
  savedBy?: string
}
import { buildFieldMappingChangesFromV3Patches } from '@/utils/draftFieldMappingChangesV3'
import { buildListingRulesChangesFromOps } from '@/utils/draftListingRulesChanges'
import {
  buildDealerPhotoDiff,
  buildDealerPhotoDraftEditRow,
  hasDealerPhotoVisualChanges,
  type DealerPhotoDiff,
} from '@/utils/dealerPhotoDiff'

const productSettings = useProductSettingsStore()
const publishStore = usePublishStore()
const accountSettings = useAccountSettingsStore()
const dealerTimeZone = computed(() => String(unref(accountSettings.dealerTimeZone) || '').trim())
const appConfig = useJazelAppConfig()
const listingStore = useListingConfigStore()

const iseBackendUrl = import.meta.env.VITE_ISE_BACKEND_URL
const isPublishing = ref(false)
const waitingForProcessing = ref(false) // Track the gap between publish request and processing start
const snackbar = ref<{
  show: boolean
  message: string
  color: 'success' | 'info' | 'warning' | 'error'
}>({
  show: false,
  message: '',
  color: 'info',
})
const isReverting = ref<Record<string, boolean>>({})
const isRevertingAny = computed(() => Object.values(isReverting.value).some(Boolean))

// Groups accordion state
const segmentsPanelsOpen = ref<string[]>([])

// Draft vehicles (pending publish)
const draftVehicles = ref<Array<{ vin: string; timestamp?: string }>>([])
const draftVehiclePanelsOpen = ref<string[]>([])
const draftVehicleDetails = ref<
  Record<
    string,
    {
      loading: boolean
      loaded: boolean
      error: string | null
      vehicle: Record<string, unknown> | null
    }
  >
>({})
const isLoadingDrafts = ref(false)
// Draft edits (VehicleDraft patches)
const draftEdits = ref<
  Array<{
    vin: string
    updatedAt?: string
    fieldKeys?: string[]
    operationalFieldKeys?: string[]
    operationalPatch?: {
      inventoryState?: InventoryState
      inventoryUsageType?: InventoryUsageType
      websiteVisibility?: WebsiteVisibility
      websiteVisibilityMode?: WebsiteVisibilityMode
      websiteVisibilityReason?: string
    }
    manualOverrideActionKeys?: string[]
    lastChangedBy?: string
    intent?: VehicleDraftIntent
    deleteRequestedAt?: string
    deleteRequestedBy?: string
  }>
>([])
type DraftEditDiffRow = {
  fieldKey: string
  beforeValue: unknown
  afterValue: unknown
  kind?: 'field' | 'operational'
  revertible?: boolean
}

type DraftEditDisplayRow = DraftEditDiffRow & {
  dealerPhotoDiff?: DealerPhotoDiff
}

const draftEditDiffs = ref<Record<string, DraftEditDiffRow[]>>({})
const draftManualOverrideRows = ref<Record<string, ManualOverrideDraftDisplayRow[]>>({})
/** Live (published) photos per VIN, used as the "before" side of dealer_photos override rows. */
const draftOverrideLivePhotos = ref<Record<string, string[] | null>>({})
const draftEditDetailsLoaded = ref<Record<string, boolean>>({})
const draftEditLoading = ref<Record<string, boolean>>({})
const draftEditError = ref<Record<string, string | null>>({})

// Groups: per-segment before/after details (published vs draft)
const segmentDiffBatchLoading = ref(false)
const segmentDiffLoading = ref<Record<string, boolean>>({})
const segmentDiffError = ref<Record<string, string | null>>({})
const segmentDiffs = ref<
  Record<
    string,
    {
      nameChanged: { from: string; to: string } | null
      logicChanged: { from: 'AND' | 'OR'; to: 'AND' | 'OR' } | null
      rulesChanged: FlatSegmentRuleChange[]
      rulesAdded: FlatSegmentRule[]
      rulesRemoved: FlatSegmentRule[]
    }
  >
>({})

// Get global processing state from App.vue
const globalIsProcessing = inject<Ref<boolean>>('isProcessing', ref(false))
const globalProcessingStatus = inject<Ref<string>>('processingStatus', ref(''))

const feedSourceConfigDraft = ref<FeedMappingDraftSummary | null>(null)
const showAllFscMatrixChanges = ref(false)
const FSC_MATRIX_PREVIEW_LIMIT = 5

const visibleFscMatrixChanges = computed(() => {
  const changes = feedSourceConfigDraft.value?.diff?.matrix.changes ?? []
  if (showAllFscMatrixChanges.value) return changes
  return changes.slice(0, FSC_MATRIX_PREVIEW_LIMIT)
})

const fscMatrixHasMore = computed(() => {
  const total = feedSourceConfigDraft.value?.diff?.matrix.total ?? 0
  return total > FSC_MATRIX_PREVIEW_LIMIT
})

const fscOverrideChangeTotal = computed(() => {
  const overrides = feedSourceConfigDraft.value?.diff?.overrides
  if (!overrides) return 0
  return overrides.added + overrides.removed + overrides.modified
})

const fscFallbackChangeTotal = computed(() => {
  const fallbacks = feedSourceConfigDraft.value?.diff?.fallbacks
  if (!fallbacks) return 0
  return fallbacks.added + fallbacks.removed + fallbacks.modified
})

function formatFscFeedLabel(feed: string | null | undefined): string {
  if (feed === null || feed === undefined || feed === '') return 'None'
  return feed
}

function formatFscChangeCountSummary(counts: {
  added: number
  removed: number
  modified: number
}): string {
  const parts: string[] = []
  if (counts.added) parts.push(`${counts.added} added`)
  if (counts.removed) parts.push(`${counts.removed} removed`)
  if (counts.modified) parts.push(`${counts.modified} modified`)
  return parts.join(', ')
}
const pausePublishByFeedDraft = ref<{ from: boolean; to: boolean } | null>(null)
const chromeCvdLocaleDraft = ref<{
  from: ChromeCvdLocale
  to: ChromeCvdLocale
} | null>(null)
const readyCourtesyStateDraft = ref<{ from: boolean; to: boolean } | null>(null)
const inventoryAlertsDraft = ref<{
  from: InventoryAlertsSettings
  to: InventoryAlertsSettings
} | null>(null)
const guardrailDraft = ref<{
  fromEnabled: boolean; toEnabled: boolean
  fromThreshold: number; toThreshold: number
} | null>(null)
const vehicleStatusFacetDraft = ref<{
  from: VehicleStatusFacetOption[]
  to: VehicleStatusFacetOption[]
} | null>(null)
type PreProcessorConfigDraft = {
  from: NormalizedPreProcessorConfig
  to: NormalizedPreProcessorConfig
}
const groupSiteConfigDraft = ref<PreProcessorConfigDraft | null>(null)
const feedConditionRulesDraftExists = ref(false)

const serverSummary = ref<UnpublishedChangesSummary | null>(null)
const serverSummaryLoading = ref(false)
const serverSummaryError = ref<string | null>(null)

const hasServerUnpublished = computed(() => serverSummary.value?.hasUnpublished === true)
const fieldMappingDraftMeta = computed(
  () => serverSummary.value?.configDrafts?.fieldMapping || null,
)
const segmentsDraftMeta = computed(() => serverSummary.value?.configDrafts?.segments || null)
const listingRulesDraftMeta = computed(
  () => serverSummary.value?.configDrafts?.inventoryMembership || null,
)
const iseSettingsDraftMeta = computed(() => serverSummary.value?.configDrafts?.iseSettings || null)
const groupSiteConfigDraftMeta = computed(
  () => serverSummary.value?.configDrafts?.groupSiteConfig || null,
)
const feedConditionRulesDraftMeta = computed(
  () => serverSummary.value?.configDrafts?.feedFieldRules || null,
)

// Use unref to get the actual values for template use
const unpublished = computed<UnpublishedState | null>(
  () => unref(productSettings.unpublished) || null,
)
// unwrap the computed from the store so this is a simple boolean
const hasChanges = computed<boolean>(() => {
  // Hybrid (server-truth-first):
  // When server summary is available, do not allow the persisted JAMS marker to resurrect ghost changes.
  if (serverSummary.value) {
    return (
      hasServerUnpublished.value ||
      draftVehicles.value.length > 0 ||
      draftEdits.value.length > 0 ||
      !!feedSourceConfigDraft.value ||
      !!pausePublishByFeedDraft.value ||
      !!chromeCvdLocaleDraft.value ||
      !!readyCourtesyStateDraft.value ||
      !!inventoryAlertsDraft.value ||
      !!vehicleStatusFacetDraft.value ||
      !!guardrailDraft.value ||
      !!groupSiteConfigDraft.value ||
      feedConditionRulesDraftExists.value
    )
  }

  // Fallback when server summary cannot be loaded (best-effort UX).
  return (
    unref(publishStore.isDirty) ||
    draftVehicles.value.length > 0 ||
    draftEdits.value.length > 0 ||
    !!feedSourceConfigDraft.value ||
    !!pausePublishByFeedDraft.value ||
    !!chromeCvdLocaleDraft.value ||
    !!readyCourtesyStateDraft.value ||
    !!inventoryAlertsDraft.value ||
    !!vehicleStatusFacetDraft.value ||
    !!guardrailDraft.value ||
    !!groupSiteConfigDraft.value ||
    feedConditionRulesDraftExists.value
  )
})

const hasUnpublishedChanges = computed(
  () => publishStore.hasAnythingToPublish && hasChanges.value,
)

const showPublishActions = computed(
  () => !initialLoading.value && !isPublishInFlight.value && hasUnpublishedChanges.value,
)
// convenience computed for button disabled state to avoid template union types
const isPublishDisabled = computed(
  () =>
    initialLoading.value ||
    isPublishing.value ||
    waitingForProcessing.value ||
    globalIsProcessing.value ||
    publishLockActive.value ||
    !hasUnpublishedChanges.value,
)
const isPublishInFlight = computed(
  // Treat the publish lock as authoritative in-flight signal so a newer terminal job (e.g. SKIPPED)
  // cannot hide the publish UX while a user publish is still running.
  () =>
    isPublishing.value ||
    waitingForProcessing.value ||
    globalIsProcessing.value ||
    publishLockActive.value,
)
const lastStatus = ref<string>('')
const statusMessage = ref<string>('')
const statusType = ref<'info' | 'success' | 'warning' | 'error'>('info')

// Publish lock status (for disabling the publish button when another publish is in progress)
const publishLockActive = ref(false)
const publishLockOwner = ref<string>('')
let lockStatusCheckInterval: ReturnType<typeof setInterval> | null = null

// Page-level initial load gate to prevent flashing empty state while data is still loading.
const initialLoading = ref(true)

const setTerminalPublishStatusMessage = (status: string) => {
  lastStatus.value = status
  if (status === 'SUCCEEDED') {
    statusMessage.value = 'Processing completed successfully!'
    statusType.value = 'success'
  } else if (status === 'SKIPPED') {
    statusMessage.value = 'Publish skipped by setting (pause_publish_by_feed=true).'
    statusType.value = 'warning'
  } else if (status === 'FLAGGED_LARGE_DELTA') {
    statusMessage.value = 'Publish flagged for review due to a large inventory change.'
    statusType.value = 'warning'
  } else {
    const publishError = publishStore.lastPublishError
    const message = Array.isArray(publishError?.messages)
      ? publishError.messages.join(' ')
      : publishError?.message
    statusMessage.value =
      publishError?.code === 'FEED_SOURCE_CONFIG_INCOMPLETE' && message
        ? message
        : 'The last publish failed. Open Publish History or View Flow for details.'
    statusType.value = 'error'
  }
}

const clearActivePublishState = (status?: string | null) => {
  const normalizedStatus = String(status || '').toUpperCase()
  isPublishing.value = false
  waitingForProcessing.value = false
  publishLockActive.value = false
  publishLockOwner.value = ''
  globalIsProcessing.value = false
  globalProcessingStatus.value = ''
  publishStore.clearPendingPublish?.()

  if (isTerminalPublishStatus(normalizedStatus)) {
    setTerminalPublishStatusMessage(normalizedStatus)
  } else if (
    statusMessage.value.includes('Another publish is currently in progress') ||
    statusMessage.value === 'Processing import feed — this may take a couple of minutes...' ||
    statusMessage.value === 'Publish request accepted. Processing will begin shortly.'
  ) {
    statusMessage.value = ''
    statusType.value = 'info'
  }
}

async function reloadAllChanges(opts?: { showLoader?: boolean }) {
  const showLoader = opts?.showLoader === true
  if (showLoader) initialLoading.value = true

  try {
    // Page content: load all change sources in parallel.
    await Promise.allSettled([
      fetchDraftVehicleEdits(),
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchReadyCourtesyStateDraft(),
      fetchInventoryAlertsDraft(),
      fetchVehicleStatusFacetDraft(),
      fetchGuardrailDraft(),
      fetchGroupSiteConfigDraft(),
      fetchFeedConditionRulesDraftStatus(),
      fetchServerUnpublishedSummary(),
      // Keep left-nav "Unpublished Changes" visibility in sync with the page's draft list.
      publishStore.refreshUnpublishedChangesSummary(),
    ])
  } finally {
    if (showLoader) initialLoading.value = false
  }
}

// Computed for showing status banner
const showStatusBanner = computed(
  () => statusMessage.value && (!hasUnpublishedChanges.value || lastStatus.value !== 'SUCCEEDED'),
)

function clearLocalUnpublishedSections() {
  draftVehicles.value = []
  draftEdits.value = []
  draftVehicleDetails.value = {}
  draftVehiclePanelsOpen.value = []
  feedSourceConfigDraft.value = null
  pausePublishByFeedDraft.value = null
  chromeCvdLocaleDraft.value = null
  readyCourtesyStateDraft.value = null
  inventoryAlertsDraft.value = null
  vehicleStatusFacetDraft.value = null
  guardrailDraft.value = null
  groupSiteConfigDraft.value = null
  feedConditionRulesDraftExists.value = false
  fieldMappingChangesServer.value = null
  segmentsChangesServer.value = null
  listingRulesChangesServer.value = null
}

// Persisted (legacy) marker-backed details.
const fieldMappingChangesPersisted = computed(() => {
  const d: any = unpublished.value?.sources?.fieldMapping?.details
  const n = Number(d?.totalModified ?? d?.fieldsChanged?.length ?? 0)
  return d && n > 0 ? d : null
})

const segmentsChangesPersisted = computed(() => {
  const d: any = unpublished.value?.sources?.segments?.details
  const n =
    Number(d?.added?.length ?? 0) +
    Number(d?.modified?.length ?? 0) +
    Number(d?.removed?.length ?? 0)
  return d && n > 0 ? d : null
})

const listingRulesChangesPersisted = computed(() => {
  const d: any = unpublished.value?.sources?.inventoryMembership?.details
  const hasTotals = Number(d?.totalRulesAdded ?? 0) > 0 || Number(d?.totalRulesRemoved ?? 0) > 0
  const hasSegments = d?.segments && Object.keys(d.segments).length > 0
  const hasChanges = d?.hasChanges === true
  return d && (hasChanges || hasTotals || hasSegments) ? d : null
})

// Server-derived details (hybrid primary).
const fieldMappingChangesServer = ref<any | null>(null)
const segmentsChangesServer = ref<any | null>(null)
const listingRulesChangesServer = ref<any | null>(null)

const fieldMappingChanges = computed(() => {
  if (serverSummary.value) {
    return serverSummary.value?.configDrafts?.fieldMapping?.exists
      ? fieldMappingChangesServer.value
      : null
  }
  return fieldMappingChangesPersisted.value
})

const effectiveSegmentsChanges = computed(() => {
  if (serverSummary.value) {
    return serverSummary.value?.configDrafts?.segments?.exists ? segmentsChangesServer.value : null
  }
  return segmentsChangesPersisted.value
})

const listingRulesChanges = computed(() => {
  if (serverSummary.value) {
    return serverSummary.value?.configDrafts?.inventoryMembership?.exists
      ? listingRulesChangesServer.value
      : null
  }
  return listingRulesChangesPersisted.value
})

const buildDraftVehiclesFromSummary = (summary: UnpublishedChangesSummary) => {
  const vins = Array.isArray(summary.draftVehicleVins) ? summary.draftVehicleVins : []
  return Array.from(
    new Set(vins.map((vin) => String(vin || '').trim().toUpperCase()).filter(Boolean)),
  ).map((vin) => ({ vin }))
}

const formatDraftVehicleDetailValue = (value: unknown) => {
  if (value == null) return ''
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : ''
  if (typeof value === 'string') return value.trim()
  return ''
}

const getDraftVehicleField = (vehicle: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = formatDraftVehicleDetailValue(vehicle[key])
    if (value) return value
  }
  return ''
}

const getDraftVehicleDetailRows = (vin: string) => {
  const vehicle = draftVehicleDetails.value[vin]?.vehicle
  if (!vehicle) return []

  return [
    { label: 'Year', value: getDraftVehicleField(vehicle, ['year', 'year_sidebar']) },
    { label: 'Make', value: getDraftVehicleField(vehicle, ['make']) },
    { label: 'Model', value: getDraftVehicleField(vehicle, ['model']) },
    { label: 'Trim', value: getDraftVehicleField(vehicle, ['trim']) },
    { label: 'Stock #', value: getDraftVehicleField(vehicle, ['stock_number', 'stockNumber']) },
    { label: 'Price', value: getDraftVehicleField(vehicle, ['display_price', 'price']) },
    { label: 'Condition', value: getDraftVehicleField(vehicle, ['condition']) },
  ].filter((row) => row.value)
}

async function fetchJamsProductConfig(productId: string): Promise<any | null> {
  const accountId = String(accountSettings.accountId || '').trim()
  const token = String(accountSettings.sessionToken || '').trim()
  const jamsBase = String(appConfig.jamsBackendUrl || '').replace(/\/+$/g, '')

  if (!accountId || !token || !jamsBase) return null

  const resp = await fetch(
    `${jamsBase}/account/products/${encodeURIComponent(accountId)}/${encodeURIComponent(productId)}`,
    {
      method: 'GET',
      headers: { authorization: `Bearer ${token}` },
    },
  )
  if (!resp.ok) return null
  const json: any = await resp.json().catch(() => null)
  if (!json) return null
  return json?.config && typeof json.config === 'object' ? json.config : json
}

async function rebuildSegmentsChangesFromServer(summary: UnpublishedChangesSummary) {
  segmentsChangesServer.value = null
  if (!summary?.configDrafts?.segments?.exists) return

  const accountId = accountSettings.accountId
  if (!iseBackendUrl || !accountId) return

  const [publishedResp, draftResp] = await Promise.all([
    fetchSegmentationConfig(String(iseBackendUrl), accountId, { includeDraft: false }),
    fetchSegmentationConfig(String(iseBackendUrl), accountId, { includeDraft: true }),
  ])

  const publishedObj = (publishedResp as any)?.data?.segments || {}
  const draftObj = (draftResp as any)?.data?.segments || {}
  const toArray = (obj: any) =>
    Object.entries(obj || {}).map(([id, seg]) => ({ id: String(id), ...(seg as any) }))

  const comparison = compareSegments(toArray(publishedObj), toArray(draftObj))
  if (!comparison?.hasChanges) return

  segmentsChangesServer.value = {
    total: comparison.total,
    timestamp: summary?.configDrafts?.segments?.savedAt || new Date().toISOString(),
    ...(comparison.added?.length ? { added: comparison.added } : {}),
    ...(comparison.modified?.length ? { modified: comparison.modified } : {}),
    ...(comparison.removed?.length ? { removed: comparison.removed } : {}),
  }
}

async function rebuildFieldMappingChangesFromServer(summary: UnpublishedChangesSummary) {
  fieldMappingChangesServer.value = null
  if (!summary?.configDrafts?.fieldMapping?.exists) return

  if (!iseBackendUrl || !accountSettings.accountId || !accountSettings.sessionToken) return

  // Load v3 draft patches (segment-scoped)
  const draft = await fetchFieldMappingDraft({
    iseEndpoint: String(iseBackendUrl),
    jazelId: String(accountSettings.accountId),
    token: String(accountSettings.sessionToken),
  })

  // V3-only: no fallback
  if ((draft as any)?.version !== 3 || !(draft as any)?.patches) {
    console.warn('[rebuildFieldMappingChangesFromServer] No v3 patches found, ignoring draft')
    return
  }

  // Load published mapping config from JAMS for baseline
  const published = await fetchJamsProductConfig('ise_srp_import_mapping')
  if (!published) return

  const details = buildFieldMappingChangesFromV3Patches({
    publishedMappingConfig: published,
    patches: (draft as any).patches,
  })

  fieldMappingChangesServer.value = details.totalModified > 0 ? details : null
}

async function rebuildListingRulesChangesFromServer(summary: UnpublishedChangesSummary) {
  listingRulesChangesServer.value = null
  if (!summary?.configDrafts?.inventoryMembership?.exists) return

  if (!iseBackendUrl || !accountSettings.accountId || !accountSettings.sessionToken) return

  // Phase 3c: Load v3 ops-based patches directly
  const draft = await fetchListingRulesDraft({
    iseEndpoint: String(iseBackendUrl),
    jazelId: String(accountSettings.accountId),
    token: String(accountSettings.sessionToken),
  })

  // Check if v3 patches exist
  if ((draft as any)?.version === 3 && (draft as any)?.patches) {
    // Build UI diff from v3 ops
    const details = buildListingRulesChangesFromOps({
      patches: (draft as any).patches,
    })
    listingRulesChangesServer.value = details.hasChanges ? details : null
    return
  }

  // Fallback for v2 patches: compare published vs effective
  const effective = await listingStore.getListingConfig().catch(() => null)
  if (!effective) return

  const pubCfg = await fetchJamsProductConfig('ise_inventory_de_listing')
  const pubAll = pubCfg?.all?.inventory_membership_rules
  const published = pubAll ? { all: { inventory_membership_rules: pubAll } } : {}

  const diff = compareListingRules(published, effective)
  listingRulesChangesServer.value = diff && diff.hasChanges ? diff : null
}

const fetchServerUnpublishedSummary = async () => {
  const endpoint = String(iseBackendUrl || '')
  const jazelId = String(accountSettings.accountId || '')
  const token = String(accountSettings.sessionToken || '')

  if (!endpoint || !jazelId || !token) {
    serverSummary.value = null
    serverSummaryError.value = null
    draftVehicles.value = []
    return
  }

  serverSummaryLoading.value = true
  serverSummaryError.value = null
  try {
    const summary = await fetchUnpublishedChangesSummary(endpoint, jazelId, token)
    serverSummary.value = summary
    draftVehicles.value = buildDraftVehiclesFromSummary(summary)

    // Server-truth reconciliation:
    // If the server says there are no drafts anywhere, clear the persisted JAMS marker to prevent
    // "ghost" Unpublished Changes surviving refresh after drafts are reverted/deleted server-side.
    if (
      shouldClearPersistedUnpublished({
        serverSummary: summary,
        hasPendingPublish: publishStore.hasPendingPublish,
        isPublishInFlight: isPublishInFlight.value,
      })
    ) {
      clearLocalUnpublishedSections()
      // NOTE: No JAMS unpublished marker persistence. Dirty state is derived from server-truth (drafts).
    }

    // Hybrid: build section details from server-truth when drafts exist.
    await Promise.all([
      rebuildSegmentsChangesFromServer(summary),
      rebuildFieldMappingChangesFromServer(summary),
      rebuildListingRulesChangesFromServer(summary),
    ])
  } catch (e: any) {
    serverSummary.value = null
    draftVehicles.value = []
    serverSummaryError.value = e?.message || 'Failed to load unpublished changes summary'
  } finally {
    serverSummaryLoading.value = false
  }
}

// (Phase 2) Legacy "rebuild if missing" is replaced by always computing server-derived details above.

const fetchDraftVehicleDetails = async (vin: string) => {
  const normalizedVin = String(vin || '').trim().toUpperCase()
  if (!normalizedVin || !iseBackendUrl || !accountSettings.accountId) return

  const existing = draftVehicleDetails.value[normalizedVin]
  if (existing?.loading || existing?.loaded) return

  draftVehicleDetails.value[normalizedVin] = {
    loading: true,
    loaded: false,
    error: null,
    vehicle: null,
  }

  try {
    const resp = await fetchVehicleByVin(iseBackendUrl, accountSettings.accountId, normalizedVin, true)
    draftVehicleDetails.value[normalizedVin] = {
      loading: false,
      loaded: true,
      error: resp.vehicle ? null : 'Vehicle details are not available yet.',
      vehicle: resp.vehicle || null,
    }
  } catch (e: any) {
    draftVehicleDetails.value[normalizedVin] = {
      loading: false,
      loaded: false,
      error: e?.message || 'Failed to load draft vehicle details',
      vehicle: null,
    }
  }
}

// Fetch VehicleDraft-based edits/deletes. New manual draft VINs come from the server summary.
const fetchDraftVehicleEdits = async () => {
  isLoadingDrafts.value = true
  try {
    try {
      const base = String(iseBackendUrl || '').replace(/\/+$/g, '')
      const url = new URL(`${base}/vehicle-drafts`)
      url.searchParams.set('jazelId', accountSettings.accountId)

      const headers: Record<string, string> = {}
      if (accountSettings.sessionToken) {
        headers.authorization = `Bearer ${accountSettings.sessionToken}`
      }

      const respDrafts = await fetch(url.toString(), { headers })
      if (respDrafts.ok) {
        const json: any = await respDrafts.json()
        const drafts = Array.isArray(json?.drafts) ? json.drafts : []
        draftEdits.value = drafts
          .map((d: any) => ({
            vin: String(d?.vin || '').trim(),
            updatedAt: d?.updatedAt,
            fieldKeys: Array.isArray(d?.fieldKeys) ? d.fieldKeys : [],
            operationalFieldKeys: Array.isArray(d?.operationalFieldKeys)
              ? d.operationalFieldKeys
              : [],
            operationalPatch:
              d?.operationalPatch && typeof d.operationalPatch === 'object'
                ? d.operationalPatch
                : undefined,
            manualOverrideActionKeys: Array.isArray(d?.manualOverrideActionKeys)
              ? d.manualOverrideActionKeys
              : [],
            lastChangedBy: d?.lastChangedBy?.username
              ? String(d.lastChangedBy.username)
              : undefined,
            intent: resolveVehicleDraftIntent(d?.intent),
            deleteRequestedAt:
              typeof d?.deleteRequestedAt === 'string' ? d.deleteRequestedAt : undefined,
            deleteRequestedBy:
              typeof d?.deleteRequestedBy?.username === 'string'
                ? String(d.deleteRequestedBy.username)
                : undefined,
          }))
          .filter((x: any) => x.vin)
      } else {
        draftEdits.value = []
      }
    } catch (e) {
      console.warn('Failed to fetch vehicle draft edits (non-fatal)', e)
      draftEdits.value = []
    }
  } catch (e) {
    console.error('Failed to fetch vehicle draft edits', e)
    draftEdits.value = []
  } finally {
    isLoadingDrafts.value = false
  }
}

const onDraftEditPanelOpen = (d: any) => {
  if (!d?.vin) return
  if (d?.intent === 'DELETE') return
  fetchDraftEditDiffs(d.vin)
}

async function cancelStagedDelete(vin: string) {
  const key = `vehicleDraft:cancelDelete:${vin}`
  try {
    setReverting(key, true)
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()
    if (!token) throw new Error('Missing session token (Authorization required)')

    await cancelVehicleDeleteDraft(
      String(iseBackendUrl),
      String(jazelId),
      String(vin || '').trim().toUpperCase(),
      token,
    )

    await reloadAllChanges().catch(() => {})
    toast('Staged removal reverted', 'success')
  } catch (e: any) {
    console.error('Failed to cancel staged removal', e)
    toast(e?.message || 'Failed to revert staged removal', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertDraftEdit(vin: string) {
  const normalizedVin = String(vin || '').trim().toUpperCase()
  const key = `vehicleDraft:revertPatch:${normalizedVin}`

  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')

    const token = String(accountSettings.sessionToken || '').trim()
    if (!token) throw new Error('Missing session token (Authorization required)')

    await revertVehicleDraft(
      String(iseBackendUrl),
      String(jazelId),
      normalizedVin,
      token,
    )

    delete draftEditDiffs.value[normalizedVin]
    delete draftManualOverrideRows.value[normalizedVin]
    delete draftEditDetailsLoaded.value[normalizedVin]
    delete draftEditLoading.value[normalizedVin]
    delete draftEditError.value[normalizedVin]

    await reloadAllChanges().catch(() => {})
    toast('Draft edits reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert draft edits', e)
    toast(e?.message || 'Failed to revert draft edits', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertDraftEditField(vin: string, fieldKey: string) {
  const normalizedVin = String(vin || '').trim().toUpperCase()
  const normalizedFieldKey = String(fieldKey || '').trim()
  if (!normalizedVin || !normalizedFieldKey) return

  const key = `vehicleDraftField:${normalizedVin}:${normalizedFieldKey}`

  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')

    const token = String(accountSettings.sessionToken || '').trim()
    if (!token) throw new Error('Missing session token (Authorization required)')

    const result = await revertVehicleDraftField(
      String(iseBackendUrl),
      String(jazelId),
      normalizedVin,
      normalizedFieldKey,
      token,
    )

    const existingRows = draftEditDiffs.value[normalizedVin] || []
    draftEditDiffs.value = {
      ...draftEditDiffs.value,
      [normalizedVin]: existingRows.filter((row) => row.fieldKey !== normalizedFieldKey),
    }

    if (result.deletedDraft) {
      delete draftEditDiffs.value[normalizedVin]
      delete draftManualOverrideRows.value[normalizedVin]
      delete draftEditDetailsLoaded.value[normalizedVin]
      delete draftEditLoading.value[normalizedVin]
      delete draftEditError.value[normalizedVin]
    }

    await reloadAllChanges().catch(() => {})
    toast('Draft field reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert draft field', e)
    toast(e?.message || 'Failed to revert draft field', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertManualOverrideDraftActionField(vin: string, fieldKey: string) {
  const normalizedVin = String(vin || '').trim().toUpperCase()
  const normalizedFieldKey = String(fieldKey || '').trim()
  if (!normalizedVin || !normalizedFieldKey) return

  const key = `vehicleDraftManualOverride:${normalizedVin}:${normalizedFieldKey}`

  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')

    const token = String(accountSettings.sessionToken || '').trim()
    if (!token) throw new Error('Missing session token (Authorization required)')

    const result = await revertManualOverrideDraftAction(
      String(iseBackendUrl),
      String(jazelId),
      normalizedVin,
      normalizedFieldKey,
      token,
    )

    const existingRows = draftManualOverrideRows.value[normalizedVin] || []
    draftManualOverrideRows.value = {
      ...draftManualOverrideRows.value,
      [normalizedVin]: existingRows.filter((row) => row.fieldKey !== normalizedFieldKey),
    }

    if (result.deletedDraft) {
      delete draftEditDiffs.value[normalizedVin]
      delete draftManualOverrideRows.value[normalizedVin]
      delete draftEditDetailsLoaded.value[normalizedVin]
      delete draftEditLoading.value[normalizedVin]
      delete draftEditError.value[normalizedVin]
    }

    await reloadAllChanges().catch(() => {})
    toast('Pending override reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert pending override', e)
    toast(e?.message || 'Failed to revert pending override', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertVehicleDraftEditsSection() {
  const key = 'section:vehicleDraftEdits'

  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')

    const token = String(accountSettings.sessionToken || '').trim()
    if (!token) throw new Error('Missing session token (Authorization required)')

    await Promise.all(
      draftEdits.value.map((d: any) => {
        const vin = String(d.vin || '').trim().toUpperCase()
        if (d?.intent === 'DELETE') {
          return cancelVehicleDeleteDraft(String(iseBackendUrl), String(jazelId), vin, token)
        }
        return revertVehicleDraft(String(iseBackendUrl), String(jazelId), vin, token)
      }),
    )

    draftEditDiffs.value = {}
    draftManualOverrideRows.value = {}
    draftEditDetailsLoaded.value = {}
    draftEditLoading.value = {}
    draftEditError.value = {}

    await reloadAllChanges().catch(() => {})
    toast('Draft Vehicle Edits section reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert draft vehicle edits section', e)
    toast(e?.message || 'Failed to revert Draft Vehicle Edits section', 'error')
  } finally {
    setReverting(key, false)
  }
}

const totalChanges = computed(() => {
  let count = 0
  if (fieldMappingChanges.value) count++
  if (effectiveSegmentsChanges.value) count++
  if (listingRulesChanges.value) count++
  if (draftVehicles.value.length > 0) count++
  if (draftEdits.value.length > 0) count++
  if (feedSourceConfigDraft.value) count++
  if (pausePublishByFeedDraft.value) count++
  if (chromeCvdLocaleDraft.value) count++
  if (readyCourtesyStateDraft.value) count++
  if (inventoryAlertsDraft.value) count++
  if (vehicleStatusFacetDraft.value) count++
  if (guardrailDraft.value) count++
  if (groupSiteConfigDraft.value) count++
  if (feedConditionRulesDraftExists.value) count++
  return count
})

const showSummaryCard = computed(() => {
  // Only show Summary if it has something meaningful to say.
  return !!unpublished.value?.lastSavedAt || totalChanges.value > 0
})

async function fetchFeedSourceConfigDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      feedSourceConfigDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    // Server-computed diff of matrix/overrides/fallbacks vs published, independent
    // of primarySources (which is derived, not a persisted draft section).
    const summary = await fetchFeedMappingDraftSummary(iseBackendUrl, jazelId, { token })
    showAllFscMatrixChanges.value = false
    feedSourceConfigDraft.value = summary.changed ? summary : null
  } catch (e) {
    console.warn('Failed to fetch feed source config draft (non-fatal):', e)
    feedSourceConfigDraft.value = null
  }
}

async function fetchPausePublishByFeedDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      pausePublishByFeedDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [published, effective] = await Promise.all([
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])

    if (effective?.source !== 'draft') {
      pausePublishByFeedDraft.value = null
      return
    }

    const from = !!(published as any)?.pause_publish_by_feed
    const to = !!(effective as any)?.pause_publish_by_feed
    if (from === to) {
      pausePublishByFeedDraft.value = null
      return
    }

    pausePublishByFeedDraft.value = { from, to }
  } catch (e) {
    console.warn('Failed to fetch pause_publish_by_feed draft (non-fatal):', e)
    pausePublishByFeedDraft.value = null
  }
}

function normalizeChromeCvdLocale(value: unknown): ChromeCvdLocale {
  return value === 'es_US' ? 'es_US' : 'en_US'
}

function formatChromeCvdLocale(locale: ChromeCvdLocale): string {
  return locale === 'es_US' ? 'Spanish' : 'English'
}

async function fetchChromeCvdLocaleDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      chromeCvdLocaleDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [published, effective] = await Promise.all([
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])

    if (effective?.source !== 'draft') {
      chromeCvdLocaleDraft.value = null
      return
    }

    const from = normalizeChromeCvdLocale(published?.chrome_cvd_locale)
    const to = normalizeChromeCvdLocale(effective?.chrome_cvd_locale)
    chromeCvdLocaleDraft.value = from === to ? null : { from, to }
  } catch (e) {
    console.warn('Failed to fetch Chrome CVD locale draft (non-fatal):', e)
    chromeCvdLocaleDraft.value = null
  }
}

async function fetchReadyCourtesyStateDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      readyCourtesyStateDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [published, effective] = await Promise.all([
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])

    if (effective?.source !== 'draft') {
      readyCourtesyStateDraft.value = null
      return
    }

    const from = !!published?.separate_ready_courtesy_vehicles
    const to = !!effective?.separate_ready_courtesy_vehicles
    readyCourtesyStateDraft.value = from === to ? null : { from, to }
  } catch (e) {
    console.warn('Failed to fetch Ready Courtesy state draft (non-fatal):', e)
    readyCourtesyStateDraft.value = null
  }
}

function formatInventoryAlertsChips(settings: InventoryAlertsSettings): string[] {
  const event = settings.events['manual_vehicle.exceeded_time_to_sale']
  const recipients = settings.recipients
    .map((recipient) => [recipient.email, recipient.phone].filter(Boolean).join(' / '))
    .filter(Boolean)
  return [
    settings.enabled ? 'Enabled' : 'Disabled',
    `Channels: ${settings.channels.join(', ') || 'none'}`,
    recipients.length > 0 ? `Recipients: ${recipients.join(', ')}` : 'Recipients: JAMS primary contact',
    event?.enabled === false ? 'Aging alert off' : 'Aging alert on',
    `Thresholds: new ${event?.thresholdDaysByCondition.new ?? 30}, used ${event?.thresholdDaysByCondition.used ?? 28}, CPO ${event?.thresholdDaysByCondition.cpo ?? 28}`,
    `Offsets: ${(event?.offsetsDays || [0, 10]).join(', ')}`,
  ]
}

async function fetchInventoryAlertsDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      inventoryAlertsDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [published, effective] = await Promise.all([
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])

    if (effective?.source !== 'draft') {
      inventoryAlertsDraft.value = null
      return
    }

    const from = normalizeInventoryAlertsSettings(published?.inventoryAlerts)
    const to = normalizeInventoryAlertsSettings(effective?.inventoryAlerts)
    inventoryAlertsDraft.value = inventoryAlertsSettingsEqual(from, to) ? null : { from, to }
  } catch (e) {
    console.warn('Failed to fetch inventory alerts draft (non-fatal):', e)
    inventoryAlertsDraft.value = null
  }
}

async function revertInventoryAlertsSection() {
  const key = 'section:inventoryAlerts'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteIseSettingsDraft(iseBackendUrl, jazelId, { section: 'inventoryAlerts', token })

    await Promise.all([
      fetchInventoryAlertsDraft(),
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchReadyCourtesyStateDraft(),
      fetchGuardrailDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Inventory alerts reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert inventory alerts section', e)
    toast(e?.message || 'Failed to revert inventory alerts', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function fetchVehicleStatusFacetDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      vehicleStatusFacetDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [published, effective] = await Promise.all([
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])

    if (effective?.source !== 'draft') {
      vehicleStatusFacetDraft.value = null
      return
    }

    const from = published.vehicleStatusFacet || []
    const to = effective.vehicleStatusFacet || []
    vehicleStatusFacetDraft.value =
      JSON.stringify(from) === JSON.stringify(to) ? null : { from, to }
  } catch (e) {
    console.warn('Failed to fetch Vehicle Status facet draft (non-fatal):', e)
    vehicleStatusFacetDraft.value = null
  }
}

async function revertFeedSourceConfigSection() {
  const key = 'section:feedSourceConfig'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    // Section-scoped revert. The backend resets only the Feed Source Config fields
    // and preserves sibling sections (pause/guardrail), deleting the draft row only
    // when nothing else differs from published.
    await deleteIseSettingsDraft(iseBackendUrl, jazelId, { section: 'feedSourceConfig', token })

    await Promise.all([
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchInventoryAlertsDraft(),
      fetchGuardrailDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Feed Source Configuration section reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert feed source config section', e)
    toast(e?.message || 'Failed to revert Feed Source Configuration section', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertPausePublishByFeedSection() {
  const key = 'section:pausePublishByFeed'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteIseSettingsDraft(iseBackendUrl, jazelId, { section: 'pausePublish', token })

    await Promise.all([
      fetchPausePublishByFeedDraft(),
      fetchFeedSourceConfigDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchReadyCourtesyStateDraft(),
      fetchInventoryAlertsDraft(),
      fetchGuardrailDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Publishing pause setting reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert pause_publish_by_feed section', e)
    toast(e?.message || 'Failed to revert publishing pause setting', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertChromeCvdLocaleSection() {
  const key = 'section:chromeCvdLocale'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteIseSettingsDraft(iseBackendUrl, jazelId, { section: 'chromeCvdLocale', token })

    await Promise.all([
      fetchChromeCvdLocaleDraft(),
      fetchReadyCourtesyStateDraft(),
      fetchInventoryAlertsDraft(),
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchGuardrailDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Chrome CVD language reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert Chrome CVD locale section', e)
    toast(e?.message || 'Failed to revert Chrome CVD language', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertReadyCourtesyStateSection() {
  const key = 'section:readyCourtesyState'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteIseSettingsDraft(iseBackendUrl, jazelId, {
      section: 'readyCourtesyState',
      token,
    })

    await Promise.all([
      fetchReadyCourtesyStateDraft(),
      fetchInventoryAlertsDraft(),
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchGuardrailDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Ready Courtesy status setting reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert Ready Courtesy state section', e)
    toast(e?.message || 'Failed to revert Ready Courtesy status setting', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertVehicleStatusFacetSection() {
  const key = 'section:vehicleStatusFacet'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteIseSettingsDraft(iseBackendUrl, jazelId, {
      section: 'vehicleStatusFacet',
      token,
    })

    await Promise.all([
      fetchVehicleStatusFacetDraft(),
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchReadyCourtesyStateDraft(),
      fetchInventoryAlertsDraft(),
      fetchGuardrailDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Vehicle Status facet reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert Vehicle Status facet section', e)
    toast(e?.message || 'Failed to revert Vehicle Status facet', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function fetchGuardrailDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      guardrailDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [published, effective] = await Promise.all([
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchIsePrimarySources(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])

    if (effective?.source !== 'draft') {
      guardrailDraft.value = null
      return
    }

    const fromEnabled = (published as any)?.feed_guardrail_enabled ?? true
    const toEnabled = (effective as any)?.feed_guardrail_enabled ?? true
    const fromThreshold = Number((published as any)?.max_allowed_net_change_percent) || 50
    const toThreshold = Number((effective as any)?.max_allowed_net_change_percent) || 50

    if (fromEnabled === toEnabled && fromThreshold === toThreshold) {
      guardrailDraft.value = null
      return
    }

    guardrailDraft.value = { fromEnabled, toEnabled, fromThreshold, toThreshold }
  } catch (e) {
    console.warn('Failed to fetch guardrail draft (non-fatal):', e)
    guardrailDraft.value = null
  }
}

async function revertGuardrailSection() {
  const key = 'section:guardrail'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteIseSettingsDraft(iseBackendUrl, jazelId, { section: 'guardrail', token })

    await Promise.all([
      fetchGuardrailDraft(),
      fetchFeedSourceConfigDraft(),
      fetchPausePublishByFeedDraft(),
      fetchChromeCvdLocaleDraft(),
      fetchInventoryAlertsDraft(),
    ])
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Guardrail settings reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert guardrail section', e)
    toast(e?.message || 'Failed to revert guardrail settings', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function fetchGroupSiteConfigDraft() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      groupSiteConfigDraft.value = null
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()

    const [publishedRes, effectiveRes] = await Promise.all([
      fetchGroupSiteConfig(iseBackendUrl, jazelId, { includeDraft: false, token }),
      fetchGroupSiteConfig(iseBackendUrl, jazelId, { includeDraft: true, token }),
    ])
    const published = publishedRes.config
    const effective = effectiveRes.config

    if (effective?.source !== 'draft') {
      groupSiteConfigDraft.value = null
      return
    }

    const publishedConfig = normalizePreProcessorConfig(published)
    const effectiveConfig = normalizePreProcessorConfig(effective)
    const publishedComparable = preProcessorConfigForCompare(publishedConfig)
    const effectiveComparable = preProcessorConfigForCompare(effectiveConfig)
    if (JSON.stringify(publishedComparable) === JSON.stringify(effectiveComparable)) {
      groupSiteConfigDraft.value = null
      return
    }

    groupSiteConfigDraft.value = { from: publishedConfig, to: effectiveConfig }
  } catch (e) {
    console.warn('Failed to fetch group site config draft (non-fatal):', e)
    groupSiteConfigDraft.value = null
  }
}

async function revertGroupSiteConfigSection() {
  const key = 'section:groupSiteConfig'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()

    await deleteGroupSiteConfigDraft(iseBackendUrl, jazelId, { token })

    await fetchGroupSiteConfigDraft()
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Group site config section reverted', 'success')
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to revert group site config section'
    console.error('Failed to revert group site config section', e)
    toast(msg, 'error')
  } finally {
    setReverting(key, false)
  }
}

async function fetchFeedConditionRulesDraftStatus() {
  try {
    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) {
      feedConditionRulesDraftExists.value = false
      return
    }
    const token = String(accountSettings.sessionToken || '').trim()
    const base = String(iseBackendUrl).replace(/\/+$/g, '')

    const url = `${base}/feed-condition-rules/${encodeURIComponent(jazelId)}?action=draftSummary`
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    if (!resp.ok) {
      feedConditionRulesDraftExists.value = false
      return
    }
    const json = await resp.json()
    feedConditionRulesDraftExists.value = json?.data?.hasDraft === true
  } catch (e) {
    console.warn('Failed to fetch feed field rules draft status (non-fatal):', e)
    feedConditionRulesDraftExists.value = false
  }
}


async function revertFeedConditionRulesSection() {
  const key = 'section:feedConditionRules'
  try {
    setReverting(key, true)

    const jazelId = accountSettings.accountId
    if (!iseBackendUrl || !jazelId) throw new Error('Missing ISE backend URL or account id')
    const token = String(accountSettings.sessionToken || '').trim()
    const base = String(iseBackendUrl).replace(/\/+$/g, '')

    const resp = await fetch(`${base}/feed-condition-rules/${encodeURIComponent(jazelId)}?configType=feedFieldRules`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!resp.ok) throw new Error(`Delete failed (${resp.status})`)

    await fetchFeedConditionRulesDraftStatus()
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Feed field rules draft reverted', 'success')
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to revert feed field rules draft'
    console.error('Failed to revert feed field rules draft', e)
    toast(msg, 'error')
  } finally {
    setReverting(key, false)
  }
}


function normalizePreProcessorConfig(
  config: Partial<GroupSiteConfigResponse> | null | undefined,
): NormalizedPreProcessorConfig {
  const mode =
    config?.mode ||
    (Array.isArray(config?.sources) && config.sources.length > 0 ? 'groupSite' : 'none')
  return {
    mode,
    sources: Array.isArray(config?.sources) ? config.sources : [],
    pattern: typeof config?.pattern === 'string' ? config.pattern : '',
    feedSource: typeof config?.feedSource === 'string' ? config.feedSource : '',
    filters: Array.isArray(config?.filters) ? config.filters : [],
    source: config?.source || 'none',
    savedAt: config?.savedAt,
    savedBy: config?.savedBy,
  }
}

function preProcessorConfigForCompare(config: NormalizedPreProcessorConfig) {
  return {
    mode: config.mode,
    sources: config.sources,
    pattern: config.pattern || '',
    feedSource: config.feedSource || '',
    filters: config.filters || [],
  }
}

function formatPreProcessorMode(mode: NormalizedPreProcessorConfig['mode']): string {
  switch (mode) {
    case 'groupSite':
      return 'Group Site'
    case 'sharedInventory':
      return 'Shared Inventory'
    case 'none':
      return 'None'
    default:
      return 'Unknown'
  }
}

function formatFilter(filter: Record<string, unknown>): string {
  const parts: string[] = []
  if (filter.condition) parts.push(String(filter.condition))
  if (filter.make) parts.push(String(filter.make))
  if (filter.model) parts.push(String(filter.model))
  if (filter.yearMin || filter.yearMax) {
    parts.push(`${filter.yearMin ?? '…'}–${filter.yearMax ?? '…'}`)
  }
  if (filter.keyword) parts.push(`keyword: ${String(filter.keyword)}`)
  if (filter.accountName) parts.push(`Account Name: ${String(filter.accountName)}`)
  return parts.join(', ') || 'no filters'
}

const toast = (message: string, color: 'success' | 'info' | 'warning' | 'error' = 'info') => {
  snackbar.value = { show: true, message, color }
}
const setReverting = (key: string, val: boolean) => {
  isReverting.value = { ...isReverting.value, [key]: val }
}

const formatValue = (v: unknown): string => {
  if (v === undefined) return '—'
  if (v === null) return 'null'
  if (typeof v === 'string') return v.length ? v : '""'
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

const operationalFieldLabels: Record<string, string> = {
  inventoryState: 'Inventory State',
  inventoryUsageType: 'Usage Type',
  websiteVisibility: 'Website Visibility',
  websiteVisibilityMode: 'Visibility Mode',
  websiteVisibilityReason: 'Visibility Reason',
}

const formatEnumLabel = (value: unknown): string => {
  if (typeof value !== 'string') return formatValue(value)
  const normalized = value.trim()
  if (!normalized) return '""'
  return normalized
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const formatDraftEditFieldLabel = (fieldKey: string): string => {
  return operationalFieldLabels[fieldKey] || formatVehicleDraftFieldLabel(fieldKey)
}

const formatDraftEditValue = (row: DraftEditDiffRow, side: 'before' | 'after' = 'before'): string => {
  const value = side === 'after' ? row.afterValue : row.beforeValue
  if (row.kind === 'operational') return formatEnumLabel(value)
  return formatManualOverrideDraftFieldValue(row.fieldKey, value, {
    dealerTimeZone: dealerTimeZone.value,
  })
}

const getDraftEditChangeCount = (draft: {
  fieldKeys?: string[]
  operationalFieldKeys?: string[]
  manualOverrideActionKeys?: string[]
}): number => countDraftVisibleChanges(draft)

const getDraftEditIntentBadge = (draft: {
  intent?: VehicleDraftIntent
  fieldKeys?: string[]
  operationalFieldKeys?: string[]
  manualOverrideActionKeys?: string[]
}) => {
  const chip = getVehicleDraftIntentChip(draft.intent)
  if (!chip) return null

  return {
    color: chip.color,
    label:
      formatVehicleDraftIntentChipLabel(draft.intent, getDraftEditChangeCount(draft)) ?? chip.label,
  }
}

const getRenderableDraftEditDiffs = (vinRaw: string): DraftEditDisplayRow[] => {
  const vin = String(vinRaw || '')
    .trim()
    .toUpperCase()
  const rows = draftEditDiffs.value[vin] || []

  return rows.flatMap((row) => {
    if (row.fieldKey !== 'dealer_photos') return [row]

    const dealerPhotoDiff = buildDealerPhotoDiff(row.beforeValue, row.afterValue)
    return hasDealerPhotoVisualChanges(dealerPhotoDiff) ? [{ ...row, dealerPhotoDiff }] : []
  })
}

const getDraftManualOverrideRows = (vinRaw: string): ManualOverrideDraftDisplayRow[] => {
  const vin = String(vinRaw || '')
    .trim()
    .toUpperCase()
  return draftManualOverrideRows.value[vin] || []
}

const getDraftOverrideLivePhotos = (vinRaw: string): string[] | null => {
  const vin = String(vinRaw || '')
    .trim()
    .toUpperCase()
  return draftOverrideLivePhotos.value[vin] ?? null
}

const hasDraftEditPanelContent = (vinRaw: string): boolean => {
  return (
    getRenderableDraftEditDiffs(vinRaw).length > 0 ||
    getDraftManualOverrideRows(vinRaw).length > 0
  )
}

const prettyRule = (r: FlatSegmentRule): string => {
  const field = String(r?.field || 'field')
  const operator = String(r?.operator || 'op')
  return `${field} ${operator} ${formatValue(r?.value)}`
}

const prettyRuleChange = (c: FlatSegmentRuleChange): string => {
  const field = String(c?.field || 'field')
  const operator = String(c?.operator || 'op')
  return `${field} ${operator}: ${formatValue(c?.fromValue)} → ${formatValue(c?.toValue)}`
}

const prefetchModifiedSegmentDiffs = async () => {
  const modified = (effectiveSegmentsChanges.value as any)?.modified
  const segIds = Array.isArray(modified)
    ? modified.map((s: any) => String(s?.id || '').trim()).filter(Boolean)
    : []
  if (segIds.length === 0) return

  // If everything is already cached, no-op
  const missing = segIds.filter((id) => !segmentDiffs.value[id] && !segmentDiffError.value[id])
  if (missing.length === 0) return

  const accountId = accountSettings.accountId
  if (!accountId) return

  segmentDiffBatchLoading.value = true
  for (const id of missing) {
    segmentDiffLoading.value = { ...segmentDiffLoading.value, [id]: true }
    segmentDiffError.value = { ...segmentDiffError.value, [id]: null }
  }

  try {
    const [publishedResp, draftResp] = await Promise.all([
      fetchSegmentationConfig(String(iseBackendUrl), accountId, { includeDraft: false }),
      fetchSegmentationConfig(String(iseBackendUrl), accountId, { includeDraft: true }),
    ])

    const publishedSegments = (publishedResp as any)?.data?.segments || {}
    const draftSegments = (draftResp as any)?.data?.segments || {}

    const nextDiffs: Record<string, any> = { ...segmentDiffs.value }
    const nextErrors: Record<string, string | null> = { ...segmentDiffError.value }

    for (const id of missing) {
      const publishedSeg = publishedSegments?.[id] || null
      const draftSeg = draftSegments?.[id] || null
      if (!publishedSeg || !draftSeg) {
        nextErrors[id] = 'Could not load before/after segment data'
        continue
      }
      nextDiffs[id] = diffSegment(publishedSeg, draftSeg)
    }

    segmentDiffs.value = nextDiffs
    segmentDiffError.value = nextErrors
  } catch (e: any) {
    for (const id of missing) {
      segmentDiffError.value = {
        ...segmentDiffError.value,
        [id]: e?.message || 'Failed to load changes',
      }
    }
  } finally {
    for (const id of missing) {
      segmentDiffLoading.value = { ...segmentDiffLoading.value, [id]: false }
    }
    segmentDiffBatchLoading.value = false
  }
}

watch(
  () => segmentsPanelsOpen.value,
  (open) => {
    const arr = Array.isArray(open) ? open : []
    if (arr.includes('segments-modified')) {
      prefetchModifiedSegmentDiffs()
    }
  },
  { deep: true },
)

const fetchDraftEditDiffs = async (vinRaw: string) => {
  const vin = String(vinRaw || '')
    .trim()
    .toUpperCase()
  if (!vin || !accountSettings.accountId) return
  if (draftEditDetailsLoaded.value[vin]) return

  draftEditLoading.value = { ...draftEditLoading.value, [vin]: true }
  draftEditError.value = { ...draftEditError.value, [vin]: null }
  try {
    const base = String(iseBackendUrl || '').replace(/\/+$/g, '')
    const url = new URL(`${base}/vehicle-drafts/history`)
    url.searchParams.set('jazelId', accountSettings.accountId)
    url.searchParams.set('vin', vin)
    url.searchParams.set('limit', '200')

    const headers: Record<string, string> = {}
    if (accountSettings.sessionToken) {
      headers.authorization = `Bearer ${accountSettings.sessionToken}`
    }

    const resp = await fetch(url.toString(), { headers })
    if (!resp.ok) throw new Error(`Failed to load history (${resp.status})`)
    const json: any = await resp.json()
    const diffs = Array.isArray(json?.diffs) ? json.diffs : []
    const baseRows: DraftEditDiffRow[] = diffs.map((d: any) => ({
      fieldKey: String(d?.fieldKey || ''),
      beforeValue: d?.beforeValue,
      afterValue: d?.afterValue,
      kind: d?.kind === 'operational' ? 'operational' : 'field',
      revertible: d?.kind === 'operational' ? false : true,
    }))

    const draftMeta = draftEdits.value.find(
      (d) =>
        String(d?.vin || '')
          .trim()
          .toUpperCase() === vin,
    )
    const baseRowKeys = new Set(baseRows.map((row) => row.fieldKey).filter(Boolean))
    const operationalRowsFromList: DraftEditDiffRow[] = Object.entries(
      draftMeta?.operationalPatch || {},
    )
      .filter(([fieldKey]) => fieldKey && !baseRowKeys.has(fieldKey))
      .map(([fieldKey, afterValue]) => ({
        fieldKey,
        beforeValue: null,
        afterValue,
        kind: 'operational',
        revertible: false,
      }))
    const manualOverrideRows = buildManualOverrideDraftDisplayRows(json?.manualOverrideActions)
    const needsDealerPhotoBaseline =
      Array.isArray(draftMeta?.fieldKeys) && draftMeta.fieldKeys.includes('dealer_photos')
    // A dealer_photos override ADD/UPDATE needs the live photos as its "before" side. This fetch only
    // feeds the override table; the field-diff dealerPhotoRow below is still built ONLY when the draft's
    // fieldKeys include dealer_photos (override-only drafts never produce a field-diff photo row).
    const needsOverridePhotoBaseline = manualOverrideRows.some(
      (row) => row.fieldKey === 'dealer_photos' && row.action !== 'REMOVE_OVERRIDE',
    )
    let dealerPhotoRow: DraftEditDiffRow | null = null
    let overrideLivePhotos: string[] | null = null

    if (needsDealerPhotoBaseline || needsOverridePhotoBaseline) {
      try {
        const [draftResp, liveResp] = await Promise.all([
          needsDealerPhotoBaseline
            ? fetchVehicleDraft(base, accountSettings.accountId, vin, accountSettings.sessionToken)
            : Promise.resolve(null),
          fetchVehicleByVin(base, accountSettings.accountId, vin, true),
        ])

        const liveDealerPhotos = (liveResp?.vehicle as Record<string, unknown> | null)?.dealer_photos
        overrideLivePhotos = Array.isArray(liveDealerPhotos)
          ? liveDealerPhotos.filter(
              (item): item is string => typeof item === 'string' && item.trim().length > 0,
            )
          : []

        if (needsDealerPhotoBaseline) {
          dealerPhotoRow = buildDealerPhotoDraftEditRow(
            liveDealerPhotos,
            draftResp?.draft?.fields?.dealer_photos?.value,
          )
        }
      } catch (photoDiffError) {
        console.warn(
          `Failed to build cumulative dealer_photos diff for ${vin}; falling back to history row`,
          photoDiffError,
        )
      }
    }

    draftEditDiffs.value = {
      ...draftEditDiffs.value,
      [vin]: [
        ...baseRows.filter((row) => row.fieldKey !== 'dealer_photos'),
        ...operationalRowsFromList,
        ...(dealerPhotoRow
          ? [dealerPhotoRow]
          : baseRows.filter((row) => row.fieldKey === 'dealer_photos' && row.fieldKey)),
      ],
    }
    draftManualOverrideRows.value = {
      ...draftManualOverrideRows.value,
      [vin]: manualOverrideRows,
    }
    draftOverrideLivePhotos.value = {
      ...draftOverrideLivePhotos.value,
      [vin]: overrideLivePhotos,
    }
    draftEditDetailsLoaded.value = { ...draftEditDetailsLoaded.value, [vin]: true }
  } catch (e: any) {
    draftEditError.value = {
      ...draftEditError.value,
      [vin]: e?.message || 'Failed to load changes',
    }
  } finally {
    draftEditLoading.value = { ...draftEditLoading.value, [vin]: false }
  }
}

function normalizeSourceId(src: any): string | null {
  if (!src) return null
  if (typeof src === 'string') return src
  if (typeof src === 'object' && typeof src.id === 'string') return src.id
  return null
}

function fieldMappingSourceList(sources: unknown): string[] {
  const list = Array.isArray(sources) ? sources : sources ? [sources] : []
  return list
    .map(normalizeSourceId)
    .filter((source): source is string => Boolean(source && source.trim()))
}

function formatFieldMappingSource(source: string): string {
  const raw = String(source || '').trim()
  const normalized = raw.toLowerCase().replace(/^import_/, '')
  if (normalized === 'vauto') return 'vAuto'
  if (normalized === 'homenet') return 'HomeNet'
  if (normalized === 'fdds1' || normalized === 'fdds') return 'FDDS'
  if (normalized === 'chrome' || normalized === 'chrome_cvd_1_0' || normalized === 'chrome_cds_v1') {
    return 'Chrome'
  }
  if (normalized === 'dealerslink') return 'DealersLink'
  if (normalized === 'manual_add' || normalized === 'custom_feed') return 'Manual add'
  return raw || 'Unknown'
}

function buildSelectionsFromMappings(mappings: any[]): Record<string, string | string[]> {
  const selections: Record<string, string | string[]> = {}
  for (const m of mappings || []) {
    const field = m?.field
    if (!field) continue
    const rawSources = Array.isArray(m?.sources) ? m.sources : []
    const ids = rawSources.map(normalizeSourceId).filter(Boolean) as string[]
    if (ids.length === 1) selections[field] = ids[0]
    else if (ids.length > 1) selections[field] = ids
  }
  return selections
}

function buildDefaultValuesFromMappings(mappings: any[]): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const m of mappings || []) {
    const field = String(m?.field || '').trim()
    if (!field) continue
    const dv = m?.default_value ?? m?.policy?.default_value
    if (dv === undefined) continue
    out[field] = dv
  }
  return out
}

function formatRuleLike(item: any) {
  // Mirrors compareConfigs.ts formatRule behavior
  let field = item?.field
  let operator = item?.operator
  let value = item?.value
  if ((!field || !operator) && Array.isArray(item?.rules) && item.rules.length > 0) {
    const first = item.rules[0]
    field = first?.field || field
    operator = first?.operator || operator || item.logic
    value = first?.value !== undefined ? first.value : value
  }
  return {
    field,
    operator: operator || item?.logic,
    value,
    action: item?.action,
    reason: item?.reason || '',
  }
}

function rulesMatchFormatted(item: any, formatted: any) {
  const f = formatRuleLike(item)
  return (
    String(f.field) === String(formatted.field) &&
    String(f.operator) === String(formatted.operator) &&
    JSON.stringify(f.value) === JSON.stringify(formatted.value) &&
    String(f.action || '') === String(formatted.action || '') &&
    String(f.reason || '') === String(formatted.reason || '')
  )
}

// Inventory revert functions removed - draft vehicles are now managed from Vehicle Details page

async function revertSegmentChange(kind: 'added' | 'modified' | 'removed', segmentId: string) {
  const key = `segments:${kind}:${segmentId}`
  try {
    setReverting(key, true)
    const accountId = accountSettings.accountId
    const token = accountSettings.sessionToken
    if (!iseBackendUrl || !accountId || !token) {
      throw new Error('Missing ISE backend URL, account id, or token')
    }

    // True patch-based revert: delete the specific patch entry server-side.
    // This works for added/modified/removed because the draft is authoritative deltas.
    await deleteSegmentDraftPatch({
      iseEndpoint: String(iseBackendUrl),
      jazelId: String(accountId),
      segmentId: String(segmentId),
      token: String(token),
    })

    await fetchServerUnpublishedSummary().catch(() => {
      // non-fatal
    })
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast(`Reverted segment ${segmentId}`, 'success')
  } catch (e: any) {
    console.error('Failed to revert segment', e)
    toast(e?.message || 'Failed to revert segment', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertSegmentsSection() {
  const key = 'section:segments'
  try {
    setReverting(key, true)
    const jazelId = accountSettings.accountId
    const token = accountSettings.sessionToken
    if (!iseBackendUrl || !jazelId || !token)
      throw new Error('Missing ISE backend URL, account id, or token')

    // Server-truth patch revert: delete the ConfigDraft domain row.
    // This avoids re-saving snapshots (which can drift in shape and re-create patches).
    await deleteSegmentsDraft({
      iseEndpoint: String(iseBackendUrl),
      jazelId: String(jazelId),
      token: String(token),
    })
    await fetchServerUnpublishedSummary().catch(() => {
      // non-fatal
    })
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Groups section reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert segments section', e)
    toast(e?.message || 'Failed to revert groups section', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertListingRuleChange(
  kind: 'added' | 'removed' | 'modified',
  segmentId: string,
  rule: any,
  idx: number,
) {
  // Phase 3c: Check if rule has opId (v3) or use legacy index-based revert (v2)
  const opId = rule?.opId
  const key = opId
    ? `listingRules:${segmentId}:${kind}:${opId}`
    : `listingRules:${segmentId}:${kind}:${idx}`

  try {
    setReverting(key, true)
    const accountId = accountSettings.accountId
    const token = accountSettings.sessionToken
    if (!iseBackendUrl || !accountId || !token) {
      throw new Error('Missing ISE backend URL, account id, or token')
    }

    if (opId) {
      // V3 per-op revert: delete specific op by opId
      await deleteListingRuleOp({
        iseEndpoint: String(iseBackendUrl),
        jazelId: String(accountId),
        segmentId: String(segmentId),
        opId: String(opId),
        token: String(token),
      })

      await fetchServerUnpublishedSummary().catch(() => {
        // non-fatal
      })
      await publishStore.refreshUnpublishedChangesSummary().catch(() => {
        // non-fatal
      })
      toast(`Reverted listing rule change`, 'success')
      return
    }

    // V2 fallback: manipulate config manually (legacy logic for v2 patches)
    const publishedCfg = (productSettings.publishedSnapshot as any)?.listingRules || {}
    const publishedRules: any[] =
      (publishedCfg?.[segmentId]?.inventory_membership_rules?.ruleset?.rules as any[]) ||
      (publishedCfg?.all?.inventory_membership_rules?.ruleset?.rules as any[]) ||
      []

    const currentCfg = (await listingStore.getListingConfig()) as any
    const nextCfg = cleanupListingRules(JSON.parse(JSON.stringify(currentCfg || {})) as any) as any

    const segKey = segmentId in nextCfg ? segmentId : 'all'
    nextCfg[segKey] = nextCfg[segKey] || {
      inventory_membership_rules: { ruleset: { logic: 'AND', rules: [] }, evaluation: {} },
    }
    nextCfg[segKey].inventory_membership_rules = nextCfg[segKey].inventory_membership_rules || {
      ruleset: { logic: 'AND', rules: [] },
    }
    nextCfg[segKey].inventory_membership_rules.ruleset = nextCfg[segKey].inventory_membership_rules.ruleset || {
      logic: 'AND',
      rules: [],
    }
    const rulesArr: any[] = Array.isArray(nextCfg[segKey].inventory_membership_rules.ruleset.rules)
      ? nextCfg[segKey].inventory_membership_rules.ruleset.rules
      : []

    if (kind === 'added') {
      const removeIdx = rulesArr.findIndex((it) => rulesMatchFormatted(it, rule))
      if (removeIdx === -1) throw new Error('Could not find the added rule in current config')
      rulesArr.splice(removeIdx, 1)
    } else if (kind === 'removed') {
      const publishedRule = publishedRules?.[rule?.index]
      if (!publishedRule) throw new Error('Could not find the removed rule in published snapshot')
      const already = rulesArr.some((it) => JSON.stringify(it) === JSON.stringify(publishedRule))
      if (!already) {
        const insertAt =
          typeof rule?.index === 'number'
            ? Math.max(0, Math.min(rulesArr.length, rule.index))
            : rulesArr.length
        rulesArr.splice(insertAt, 0, publishedRule)
      }
    } else if (kind === 'modified') {
      const publishedRule = publishedRules?.[rule?.index]
      if (!publishedRule) throw new Error('Could not find the modified rule in published snapshot')
      const at = typeof rule?.index === 'number' ? rule.index : idx
      if (at < 0 || at >= rulesArr.length) throw new Error('Modified rule index out of range')
      rulesArr[at] = publishedRule
    }

    nextCfg[segKey].inventory_membership_rules.ruleset.rules = rulesArr
    const ok = await listingStore.saveListingConfig(nextCfg)
    if (!ok) throw new Error('Failed to save publish rules')

    await productSettings.removeUnpublishedSource('listingRules')
    toast(`Reverted listing rule change`, 'success')
  } catch (e: any) {
    console.error('Failed to revert listing rule', e)
    toast(e?.message || 'Failed to revert listing rule', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertListingRulesSection() {
  const key = 'section:listingRules'
  try {
    setReverting(key, true)
    const jazelId = accountSettings.accountId
    const token = accountSettings.sessionToken
    if (!iseBackendUrl || !jazelId || !token)
      throw new Error('Missing ISE backend URL, account id, or token')

    // Phase 3c: Server-truth patch revert: delete the ConfigDraft domain row.
    await deleteListingRulesDraft({
      iseEndpoint: String(iseBackendUrl),
      jazelId: String(jazelId),
      token: String(token),
    })

    await fetchServerUnpublishedSummary().catch(() => {
      // non-fatal
    })
    await publishStore.refreshUnpublishedChangesSummary().catch(() => {
      // non-fatal
    })
    toast('Publish Rules section reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert publish rules section', e)
    toast(e?.message || 'Failed to revert publish rules section', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertFieldMappingChange(change: any) {
  const fieldKey = String(change?.fieldKey || change?.field || '').trim()
  const segmentId = String(change?.segmentId || 'all').trim()
  const key = `fieldMapping:${segmentId}:${fieldKey}`
  if (!fieldKey) return

  try {
    setReverting(key, true)
    const accountId = accountSettings.accountId
    const token = accountSettings.sessionToken
    if (!iseBackendUrl || !accountId || !token) {
      throw new Error('Missing ISE backend URL, account id, or token')
    }

    // V3 per-field revert: delete specific field from segment-scoped patches
    await deleteFieldMappingV3Field({
      iseEndpoint: String(iseBackendUrl),
      jazelId: String(accountId),
      segmentId,
      fieldKey,
      token: String(token),
    })

    await fetchServerUnpublishedSummary().catch(() => {
      // non-fatal
    })

    toast(`Reverted field mapping for ${fieldKey}`, 'success')
  } catch (e: any) {
    console.error('Failed to revert field mapping', e)
    toast(e?.message || 'Failed to revert field mapping change', 'error')
  } finally {
    setReverting(key, false)
  }
}

async function revertFieldMappingSection() {
  const key = 'section:fieldMapping'
  try {
    setReverting(key, true)
    const jazelId = accountSettings.accountId
    const token = accountSettings.sessionToken
    if (!iseBackendUrl || !jazelId || !token)
      throw new Error('Missing ISE backend URL, account id, or token')

    // V3 server-truth: delete the ConfigDraft domain row
    const base = String(iseBackendUrl).replace(/\/+$/g, '')
    const url = `${base}/drafts/field-mapping?jazelId=${encodeURIComponent(jazelId)}`

    const resp = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (!resp.ok) {
      const text = await resp.text().catch(() => '')
      throw new Error(text || `Failed to revert field mapping draft (${resp.status})`)
    }

    await fetchServerUnpublishedSummary().catch(() => {
      // non-fatal
    })

    toast('Field Mapping section reverted', 'success')
  } catch (e: any) {
    console.error('Failed to revert field mapping section', e)
    toast(e?.message || 'Failed to revert field mapping section', 'error')
  } finally {
    setReverting(key, false)
  }
}

// Watch for global pipeline state changes.
// This must handle "fast terminal" jobs where polling never observes INITIATED/PROCESSING.
watch(
  [globalIsProcessing, globalProcessingStatus, publishLockActive],
  ([isProcessing, status, lockActive]) => {
    const s = String(status || '').toUpperCase()
    const isTerminal = isTerminalPublishStatus(s)

    if (isProcessing && waitingForProcessing.value) {
      console.log('✅ Processing detected, clearing waitingForProcessing flag')
      waitingForProcessing.value = false
      statusMessage.value = 'Processing import feed — this may take a couple of minutes...'
      statusType.value = 'info'
      lastStatus.value = 'PROCESSING'
      return
    }

    // If we were waiting for processing to start but the job already finished between polls,
    // clear the waiting flag and show the terminal status.
    if (!isProcessing && waitingForProcessing.value && isTerminal && !lockActive) {
      clearActivePublishState(s)
      if (s === 'SUCCEEDED') {
        reloadAllChanges().catch(() => {
          // non-fatal
        })
        // Re-check lock status immediately so the publish button state updates without waiting for poll interval.
        checkLockStatus()
      }
      return
    }

    // Normal completion path: we observed PROCESSING, and now global processing stopped.
    if (!isProcessing && !lockActive && lastStatus.value === 'PROCESSING') {
      statusMessage.value = 'Processing completed successfully!'
      statusType.value = 'success'
      lastStatus.value = 'SUCCEEDED'
      reloadAllChanges().catch(() => {
        // non-fatal
      })
      // Re-check lock status immediately so the publish button state updates without waiting for poll interval.
      checkLockStatus()
    }
  },
)

// Check publish lock status to disable button when another publish is in progress
const checkLockStatus = async () => {
  try {
    const lockStatus = await checkPublishLockStatus(iseBackendUrl, accountSettings.accountId)
    publishLockActive.value = lockStatus.locked === true
    publishLockOwner.value = lockStatus.ownerCorrelationId || ''

    // Show message if locked
    if (lockStatus.locked === true) {
      const pageInitiatedPublish =
        waitingForProcessing.value ||
        globalIsProcessing.value ||
        lastStatus.value === 'INITIATED' ||
        lastStatus.value === 'PROCESSING'
      const hasOptimisticSuccessMessage =
        statusType.value === 'success' ||
        statusMessage.value.includes('Processing completed successfully!') ||
        statusMessage.value.includes('Publish request accepted.')

      if (pageInitiatedPublish || hasOptimisticSuccessMessage) {
        statusMessage.value = 'Processing import feed — this may take a couple of minutes...'
        statusType.value = 'info'
        if (lastStatus.value === 'INITIATED') lastStatus.value = 'PROCESSING'
      } else if (!statusMessage.value) {
        statusMessage.value =
          'Another publish is currently in progress. Please wait for it to finish.'
        statusType.value = 'info'
      }
      return
    }

    const pipeline = await checkPipelineStatus(iseBackendUrl, accountSettings.accountId).catch(
      () => null,
    )
    const latestStatus = String(pipeline?.status || '').toUpperCase()

    if (!shouldClearActivePublishForUnlockedStatus(latestStatus)) {
      globalIsProcessing.value = true
      globalProcessingStatus.value = latestStatus
      statusMessage.value = 'Processing import feed — this may take a couple of minutes...'
      statusType.value = 'info'
      lastStatus.value = latestStatus
      if (waitingForProcessing.value) waitingForProcessing.value = false
      return
    }

    const hadActivePublishState =
      isPublishing.value ||
      waitingForProcessing.value ||
      globalIsProcessing.value ||
      publishLockActive.value ||
      publishStore.hasPendingPublish === true ||
      statusMessage.value.includes('Another publish is currently in progress') ||
      statusMessage.value === 'Processing import feed — this may take a couple of minutes...' ||
      statusMessage.value === 'Publish request accepted. Processing will begin shortly.'
    const shouldRefreshAfterClear =
      hadActivePublishState ||
      (isTerminalPublishStatus(latestStatus) && lastStatus.value !== latestStatus)

    clearActivePublishState(latestStatus)

    if (shouldRefreshAfterClear) {
      reloadAllChanges().catch(() => {
        // non-fatal
      })
    }
  } catch (error) {
    console.error('Error checking publish lock status:', error)
    // Non-fatal: assume unlocked on error
    publishLockActive.value = false
  }
}

const startLockStatusPolling = () => {
  // Check immediately
  checkLockStatus()

  // Then check every 10 seconds
  if (!lockStatusCheckInterval) {
    lockStatusCheckInterval = setInterval(checkLockStatus, 10000)
  }
}

const stopLockStatusPolling = () => {
  if (lockStatusCheckInterval) {
    clearInterval(lockStatusCheckInterval)
    lockStatusCheckInterval = null
  }
}

// Fetch draft vehicles on mount
onMounted(() => {
  reloadAllChanges({ showLoader: true }).catch(() => {
    // non-fatal
  })

  // Start polling lock status
  startLockStatusPolling()
})

onBeforeUnmount(() => {
  stopLockStatusPolling()
})

// Async Publish Preview validation
// -----------------------------------------------------------------------------
// Refresh the latest preview validity on mount + after every save (the
// `markDirty` flow already triggers a draft refresh; we rely on that). On
// click the gate decides whether to open the warning dialog or publish
// directly.
const previewStore = usePublishPreviewStore()
const showPublishWithoutPreviewDialog = ref(false)
const router = useRouter()

const handlePublishClick = async () => {
  if (!PUBLISH_PREVIEW_ENABLED) {
    // TEMPORARY:
    // Preview gating is bypassed while Publish Preview is disabled.
    // Normal publish behavior must remain unchanged.
    await handlePublish()
    return
  }

  // Existing Publish Preview validation/gating behavior remains below.
  // Re-resolve validity right before publish so we don't act on a cached
  // store value. The /latest snapshot reflects the dealer's current
  // draftHash so this catches edits made on another tab too.
  if (accountSettings.accountId && accountSettings.sessionToken) {
    await previewStore.refreshLatest({
      endpoint: iseBackendUrl,
      jazelId: accountSettings.accountId,
      sessionToken: accountSettings.sessionToken,
    })
  }

  if (previewStore.requirePreviewBeforePublish()) {
    return handlePublish()
  }

  showPublishWithoutPreviewDialog.value = true
}

const handlePublishAnyway = async () => {
  showPublishWithoutPreviewDialog.value = false
  await handlePublish()
}

const handleRunPreviewFromDialog = () => {
  showPublishWithoutPreviewDialog.value = false
  router.push('/publish-preview')
}

const handlePublish = async () => {
  try {
    isPublishing.value = true
    statusMessage.value = 'Publishing changes...'
    statusType.value = 'info'

    const success = await publishStore.publishChanges(iseBackendUrl, accountSettings.accountId)

    if (success) {
      statusMessage.value = 'Publish request accepted. Processing will begin shortly.'
      statusType.value = 'info'
      lastStatus.value = 'INITIATED'

      // Keep button disabled while waiting for processing to start
      waitingForProcessing.value = true
      clearLocalUnpublishedSections()
      await publishStore.refreshUnpublishedChangesSummary().catch(() => {
        // non-fatal
      })

      // The global polling in App.vue will detect the processing state
      // and keep the button disabled until processing completes
    } else {
      const publishError = publishStore.lastPublishError
      const message = Array.isArray(publishError?.messages)
        ? publishError.messages.join(' ')
        : publishError?.message
      statusMessage.value =
        publishError?.code === 'FEED_SOURCE_CONFIG_INCOMPLETE' && message
          ? message
          : 'Failed to publish changes. Please try again.'
      statusType.value = 'error'
      lastStatus.value = 'FAILED'
    }
  } catch (error) {
    console.error('Error publishing changes:', error)
    statusMessage.value = 'An error occurred while publishing. Please try again.'
    statusType.value = 'error'
    lastStatus.value = 'FAILED'
  } finally {
    isPublishing.value = false
  }
}
</script>

<style scoped>
.field-mapping-source-change {
  overflow: visible;
  white-space: normal;
}

.field-mapping-source-change__row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-bottom: 4px;
}

.field-mapping-source-change__label {
  min-width: 104px;
}

.fsc-diff-list {
  list-style: disc;
  margin: 0;
  padding-left: 1.25rem;
}
</style>
